<template>
  <section class="center-panel">
    <article class="screen-card map-card">
      <div class="map-topline">
        <div class="duty-item">
          <span class="dot"></span>
          <span class="duty-text" :title="resolvedDutyInfo.leader">带班领导：{{ resolvedDutyInfo.leader }}</span>
        </div>
        <div class="duty-item">
          <span class="dot"></span>
          <span class="duty-text" :title="resolvedDutyInfo.employee">值班关员：{{ resolvedDutyInfo.employee }}</span>
        </div>
      </div>

      <div class="region-switch">
        <button
          v-for="(item, index) in displayRegions"
          :key="item.name"
          type="button"
          :class="{ active: item.active }"
          :aria-pressed="item.active ? 'true' : 'false'"
          @click="switchRegion(index)"
        >
          <img v-if="item.iconUrl" class="region-switch__icon" :src="item.iconUrl" alt="" />
          <span class="region-switch__label">{{ item.name }}</span>
        </button>
      </div>

      <div class="map-stage">
        <img
          v-if="mapImage"
          class="map-image"
          :src="mapImage"
          :alt="activeMapMode === 'biosecurity' ? '国门生物安全检测地图' : `${currentRegionName || '乌昌区域'}地图`"
          @error="handleMapImageError"
        />

        <div v-if="shouldShowRegionDescription" class="map-left-description">
          <div class="map-left-description__text">{{ currentRegionDescription }}</div>
        </div>

        <div class="map-side-panel">
          <div class="map-side-tag">
            <button
              type="button"
              class="side-tag-btn side-tag-title"
              :class="{ active: activeMapMode === 'overview' }"
              @click="switchMapMode('overview')"
            >乌昌海关辖区概况</button>
            <button
              type="button"
              class="side-tag-btn side-tag-subtitle"
              :class="{ active: activeMapMode === 'biosecurity' }"
              @click="switchMapMode('biosecurity')"
            >国门生物安全监测</button>
          </div>
        </div>

        <ul class="map-legend">
          <li v-for="item in displayLegends" :key="item.name">
            <img
              v-if="item.iconUrl && !isColorValue(item.iconUrl)"
              class="legend-icon"
              :src="item.iconUrl"
              :alt="item.name"
              @error="handleLegendIconError(item.name)"
            />
            <span
              v-else
              class="legend-dot"
              :style="getLegendMarkerStyle(item)"
            ></span>
            <span>{{ item.name }}</span>
          </li>
        </ul>
      </div>
    </article>

    <WlRegionDialog
      :visible="showWlDialog"
      :period-key="periodKey"
      :date-range="dateRange"
      @close="closeWlDialog"
    />

    <BondedRegionDialog
      :visible="showBondedDialog"
      :period-key="periodKey"
      :date-range="dateRange"
      @close="closeBondedDialog"
    />

    <div class="center-bottom">
      <article class="screen-card table-card table-card--promoted">
        <TabHeader :items="['海关监控预警']" :activeIndex="0">
          <div class="table-tabs">
            <span
              v-for="(tab, index) in tableTabs"
              :key="tab"
              class="table-tab"
              :class="{
                active: index === tableTabIdx,
                'has-new': warningNewFlags[warningTables[index].key]
              }"
              @click="selectWarningTab(index)"
            >
              <span>{{ tab }}</span>
              <i
                v-if="warningNewFlags[warningTables[index].key]"
                class="table-tab__new-dot"
                aria-hidden="true"
              ></i>
            </span>
          </div>
        </TabHeader>
        <div ref="warningTableWrap" class="info-table-wrap" :aria-busy="warningLoading">
          <table class="info-table" :style="{ minWidth: currentWarningTable.minWidth || '100%' }">
            <colgroup>
              <col
                v-for="column in currentWarningTable.columns"
                :key="`warning-col-${column.key}`"
                :style="getWarningColumnStyle(column)"
              >
            </colgroup>
            <thead>
              <tr>
                <th
                  v-for="column in currentWarningTable.columns"
                  :key="column.key"
                  :class="{ 'info-table-col--wrap': column.wrap }"
                  :style="getWarningColumnStyle(column)"
                >
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in currentWarningTable.rows"
                :key="`${currentWarningTable.key}-${row.recordId || row.id}`"
              >
                <td
                  v-for="column in currentWarningTable.columns"
                  :key="column.key"
                  :class="getWarningColumnClass(column)"
                  :style="getWarningColumnStyle(column)"
                >
                  {{ row[column.key] }}
                </td>
              </tr>
              <tr v-if="!currentWarningTable.rows.length">
                <td :colspan="currentWarningTable.columns.length" class="warning-empty">
                  {{ warningLoading ? '加载中...' : warningErrors[currentWarningTable.key] || '暂无数据' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <el-pagination
          :key="currentWarningTable.key"
          class="warning-pagination"
          small
          :current-page="currentWarningPage.pageNum"
          :page-size="currentWarningPage.pageSize"
          :total="currentWarningPage.total"
          :pager-count="5"
          layout="total, prev, pager, next, jumper"
          @current-change="changeWarningPage"
        />
      </article>

      <div class="shortcut-grid" role="list">
        <button
          v-for="item in shortcutCards"
          :key="item.key"
          type="button"
          class="shortcut-card"
          :class="{ active: activeShortcutKey === item.key }"
          role="listitem"
          @click="openShortcutDialog(item)"
        >
          <span class="shortcut-card__label">{{ item.label }}</span>
        </button>
      </div>
    </div>

    <div v-if="activeShortcut" class="shortcut-dialog-layer" @click.self="closeShortcutDialog">
      <TanchuangFrame :title="shortcutDialogTitle" v-slot="{ backgroundStyle }">
        <section class="shortcut-dialog" :class="`shortcut-dialog--${activeShortcut.key}`" :style="backgroundStyle">
          <div v-if="activeShortcut.key !== 'bonded'" class="shortcut-dialog__tools">
            <ScreenDateRangePicker
              v-if="isShortcutDateRange"
              v-model="shortcutDateRanges[activeShortcutKey]"
              @change="fetchShortcutData"
            />
            <div v-else class="shortcut-period-switch">
              <button
                v-for="(item, index) in shortcutPeriodTabs"
                :key="item"
                type="button"
                :class="{ active: index === shortcutPeriodIndex }"
                @click="switchShortcutPeriod(index)"
              >
                {{ item }}
              </button>
            </div>
          </div>

          <template v-if="activeShortcut.key === 'tax'">
            <TabHeader :items="['税收金额']" :activeIndex="0"></TabHeader>
            <div class="taxation-dialog-body">
              <div ref="taxationChart" class="taxation-chart"></div>
            </div>
          </template>

          <template v-else-if="activeShortcut.key === 'grain'">
            <TabHeader :items="['粮食检疫监管']" :activeIndex="0">
              <div class="grain-tab-group">
                <span
                  v-for="(tab, index) in grainViewTabs"
                  :key="tab.key"
                  class="tab-btn grain-tab-btn"
                  :class="{ active: index === grainViewIndex }"
                  @click="switchGrainView(index)"
                >{{ tab.label }}</span>
              </div>
            </TabHeader>

            <div v-if="grainViewKey === 'details'" class="grain-details-body">
              <div class="grain-details-table">
                <div class="grain-details-table__head">
                  <span>日期</span>
                  <span>调运单数</span>
                  <span>实际调入重量（吨）</span>
                </div>
                <div
                  v-for="group in pagedGrainDetailGroups"
                  :key="group.id || group.date"
                  class="grain-details-group"
                >
                  <button
                    type="button"
                    class="grain-details-table__row"
                    :class="{ 'is-expanded': isGrainDateExpanded(group) }"
                    @click="toggleGrainDate(group)"
                  >
                    <span class="grain-date-cell">
                      <i class="grain-expand-icon"></i>
                      <span>{{ group.date || '-' }}</span>
                    </span>
                    <strong>{{ group.transferCount || 0 }}</strong>
                    <span>{{ group.actualTransferTotal || 0 }}</span>
                  </button>

                  <div v-if="isGrainDateExpanded(group)" class="grain-transfer-panel">
                    <div class="grain-transfer-table">
                      <div class="grain-transfer-table__head">
                        <span>发生日期</span>
                        <span>报关单号</span>
                        <span>调入企业</span>
                        <span>调运重量（吨）</span>
                        <span>实际调入重量（吨）</span>
                        <span>原产国</span>
                        <span>品种</span>
                      </div>
                      <div
                        v-for="transfer in group.transfers || []"
                        :key="`${group.id}-${transfer.id}`"
                        class="grain-transfer-group"
                      >
                        <div
                          class="grain-transfer-table__row"
                          :class="{ 'is-clickable': transfer.useCount }"
                          :role="transfer.useCount ? 'button' : undefined"
                          :tabindex="transfer.useCount ? 0 : -1"
                          @click="openGrainUseRecordDialog(group, transfer)"
                          @keydown.enter.prevent="openGrainUseRecordDialog(group, transfer)"
                        >
                          <span>{{ transfer.sentDate || '-' }}</span>
                          <span class="grain-cell--wrap">{{ transfer.trackingNumber || '-' }}</span>
                          <span class="grain-cell--wrap">{{ transfer.transferEnterprise || '-' }}</span>
                          <span>{{ transfer.transferNum || '-' }}</span>
                          <span>{{ transfer.actualTransferNum || '-' }}</span>
                          <span class="grain-cell--wrap">{{ transfer.countryOrigin || '-' }}</span>
                          <span class="grain-cell--wrap">{{ transfer.variety || '-' }}</span>
                        </div>
                      </div>
                      <div v-if="!(group.transfers || []).length" class="grain-detail-empty">暂无调运单记录</div>
                    </div>
                  </div>
                </div>
                <div v-if="!resolvedGrainDetailGroups.length" class="grain-detail-empty">暂无数据</div>
              </div>

              <footer class="grain-detail-pagination">
                <span>共 {{ resolvedGrainDetailGroups.length }} 个日期</span>
                <button
                  type="button"
                  :disabled="grainDetailPageNum <= 1"
                  @click="changeGrainDetailPage(grainDetailPageNum - 1)"
                >上一页</button>
                <strong>第 {{ grainDetailPageNum }} / {{ grainDetailTotalPages }} 页</strong>
                <button
                  type="button"
                  :disabled="grainDetailPageNum >= grainDetailTotalPages"
                  @click="changeGrainDetailPage(grainDetailPageNum + 1)"
                >下一页</button>
              </footer>
            </div>

            <div v-else class="grain-dialog-body">
              <div ref="grainChart" class="grain-dialog-chart"></div>
            </div>
          </template>

          <template v-else-if="activeShortcut.key === 'trade'">
            <TabHeader :items="['外贸情况']" :activeIndex="0">
              <div class="trade-toolbar">
                <div class="pie-tabs">
                  <span
                    v-for="(btn, i) in tradeBtns"
                    :key="btn"
                    class="tab-btn"
                    :class="{ active: i === tradeBtnIdx }"
                    @click="tradeBtnIdx = i"
                  >{{ btn }}</span>
                </div>
              </div>
            </TabHeader>
            <div class="trade-content trade-content--dialog">
              <div class="trade-network-panel">
                <div class="trade-network">
                  <span class="trade-network-center">乌昌海关</span>
                  <div
                    v-for="item in tradeBubbleNodes"
                    :key="item.key"
                    class="trade-bubble-group"
                  >
                    <span class="trade-network-link" :style="item.lineStyle"></span>
                    <div class="trade-bubble" :style="item.style">
                      <strong>{{ item.region }}</strong>
                      <span>{{ item.value }}（亿）</span>
                      <em>同比 {{ item.pct }}%</em>
                    </div>
                  </div>
                  <div v-if="!tradeBubbleNodes.length" class="trade-empty">暂无数据</div>
                </div>
              </div>

              <div class="trade-funnel-panel">
                <div class="trade-funnel-title">前五名商品</div>
                <div class="trade-funnel">
                  <div
                    v-for="item in tradeFunnelItems"
                    :key="item.key"
                    class="trade-funnel-item"
                    :style="item.style"
                    :title="item.title"
                  >
                    <span class="trade-funnel-name">{{ item.name }}</span>
                    <span class="trade-funnel-value">{{ item.value }} {{ item.unit }}</span>
                  </div>
                  <div v-if="!tradeFunnelItems.length" class="trade-empty trade-empty--funnel">暂无数据</div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="activeShortcut.key === 'audit'">
            <TabHeader :items="['稽核查作业情况']" :activeIndex="0"></TabHeader>
            <div class="audit-dialog-panel">
              <div class="audit-summary-row">
                <div class="audit-summary-card audit-summary-card--pending">
                  <span>在办</span>
                  <strong>{{ auditSummary.pending }}</strong>
                </div>
                <div class="audit-summary-card audit-summary-card--done">
                  <span>已办</span>
                  <strong>{{ auditSummary.done }}</strong>
                </div>
                <div class="audit-summary-card audit-summary-card--total">
                  <span>合计</span>
                  <strong>{{ auditSummary.total }}</strong>
                </div>
              </div>
              <div class="audit-card-grid">
                <article
                  v-for="item in resolvedAuditCards"
                  :key="item.label"
                  class="audit-item-card"
                >
                  <div class="audit-item-head">
                    <span class="audit-item-title">
                      <i :style="{ background: item.color }"></i>
                      {{ item.label }}
                    </span>
                    <span class="audit-item-total">合计 {{ item.total }}</span>
                  </div>
                  <div class="audit-metric-row">
                    <span>在办</span>
                    <div class="audit-meter">
                      <i
                        :style="{
                          width: item.pendingPct + '%',
                          background: item.pendingGradient
                        }"
                      ></i>
                    </div>
                    <strong>{{ item.pendingCount }}</strong>
                  </div>
                  <div class="audit-metric-row audit-metric-row--done">
                    <span>已办</span>
                    <div class="audit-meter">
                      <i
                        :style="{
                          width: item.donePct + '%',
                          background: item.doneGradient
                        }"
                      ></i>
                    </div>
                    <strong>{{ item.doneCount }}</strong>
                  </div>
                </article>
              </div>
            </div>
          </template>

          <template v-else-if="activeShortcut.key === 'onduty'">
            <TabHeader :items="['在岗情况']" :activeIndex="0"></TabHeader>
            <div class="onduty-dialog-body">
              <div class="onduty-table">
                <div class="onduty-table__header">
                  <span>科室</span>
                  <span>科室、关员在岗率</span>
                  <span>协勤在岗率</span>
                  <span>日期</span>
                </div>
                <div
                  v-for="(item, index) in resolvedOnDutyRows"
                  :key="item.id || `onduty-${index}`"
                  class="onduty-table__group"
                >
                  <button
                    type="button"
                    class="onduty-table__row"
                    :class="{ 'is-expanded': isDutyDepartmentExpanded(item) }"
                    @click="toggleDutyDepartment(item)"
                  >
                    <span class="onduty-dept-cell">
                      <i class="onduty-expand-icon"></i>
                      <span>{{ item.dept || '' }}</span>
                    </span>
                    <strong>{{ item.formalPct || item.pct || '' }}</strong>
                    <strong>{{ item.auxiliaryPct || item.auxPct || '' }}</strong>
                    <span>{{ item.date || '' }}</span>
                  </button>

                  <div v-if="isDutyDepartmentExpanded(item)" class="onduty-expand-panel">
                    <template v-if="isDutyDailyPeriod">
                      <div class="onduty-detail-head onduty-detail-grid--daily">
                        <span>姓名</span>
                        <span>在岗状态</span>
                        <span>日期</span>
                      </div>
                      <div
                        v-for="record in item.records || []"
                        :key="`daily-${item.id}-${record.id}`"
                        class="onduty-detail-row onduty-detail-grid--daily"
                      >
                        <span>{{ record.name || '未填写姓名' }}</span>
                        <span :class="getDutyStatusClass(record.status)">{{ record.status || '' }}</span>
                        <span>{{ record.date || '' }}</span>
                      </div>
                      <div v-if="!(item.records || []).length" class="onduty-detail-empty">暂无人员记录</div>
                    </template>

                    <template v-else>
                      <div class="onduty-detail-head onduty-detail-grid--people">
                        <span>姓名</span>
                        <span>类型</span>
                        <span>在岗率</span>
                        <span>查看记录</span>
                      </div>
                      <button
                        v-for="person in item.people || []"
                        :key="`${item.id}-${person.id || person.name}`"
                        type="button"
                        class="onduty-person-row onduty-detail-grid--people"
                        @click="openDutyRecordDialog(item, person)"
                      >
                        <span>{{ person.name || '未填写姓名' }}</span>
                        <span class="onduty-person-type">{{ person.userType || '正式' }}</span>
                        <strong>{{ person.pct || '' }}</strong>
                        <span class="onduty-record-link">明细 <i></i></span>
                      </button>
                      <div v-if="!(item.people || []).length" class="onduty-detail-empty">暂无人员数据</div>
                    </template>
                  </div>
                </div>
                <div v-if="!resolvedOnDutyRows.length" class="shortcut-empty">暂无数据</div>
              </div>
            </div>
          </template>

          <template v-else-if="activeShortcut.key === 'bonded'">
            <div class="shortcut-blank" aria-hidden="true"></div>
          </template>

          <template v-else-if="activeShortcut.key === 'barter'">
            <TabHeader :items="['易货贸易']" :activeIndex="0"></TabHeader>
            <div class="barter-dialog-body">
              <div ref="barterChart" class="barter-chart"></div>
            </div>
          </template>

          <template v-else>
            <TabHeader :items="[activeShortcut.label]" :activeIndex="0"></TabHeader>
            <div class="shortcut-empty">暂无数据</div>
          </template>
        </section>
      </TanchuangFrame>

      <div v-if="dutyRecordDialog.visible" class="duty-record-layer" @click.self="closeDutyRecordDialog">
        <section class="duty-record-dialog" role="dialog" aria-modal="true" :aria-label="dutyRecordDialog.title">
          <header class="duty-record-dialog__header">
            <div>
              <span>人员在岗记录</span>
              <strong>{{ dutyRecordDialog.title }}</strong>
            </div>
            <button type="button" class="duty-record-dialog__close" aria-label="关闭" @click="closeDutyRecordDialog">×</button>
          </header>

          <div class="duty-record-table">
            <div class="duty-record-table__head">
              <span>序号</span>
              <span>姓名</span>
              <span>在岗状态</span>
              <span>日期</span>
            </div>
            <div
              v-for="(record, index) in pagedDutyRecordRows"
              :key="`record-dialog-${record.id}-${index}`"
              class="duty-record-table__row"
            >
              <span>{{ (dutyRecordDialog.pageNum - 1) * dutyRecordDialog.pageSize + index + 1 }}</span>
              <span>{{ record.name || '未填写姓名' }}</span>
              <span :class="getDutyStatusClass(record.status)">{{ record.status || '' }}</span>
              <span>{{ record.date || '' }}</span>
            </div>
            <div v-if="!pagedDutyRecordRows.length" class="duty-record-empty">暂无记录</div>
          </div>

          <footer class="duty-record-pagination">
            <span>共 {{ dutyRecordDialog.rows.length }} 条</span>
            <button
              type="button"
              :disabled="dutyRecordDialog.pageNum <= 1"
              @click="changeDutyRecordPage(dutyRecordDialog.pageNum - 1)"
            >上一页</button>
            <strong>第 {{ dutyRecordDialog.pageNum }} / {{ dutyRecordTotalPages }} 页</strong>
            <button
              type="button"
              :disabled="dutyRecordDialog.pageNum >= dutyRecordTotalPages"
              @click="changeDutyRecordPage(dutyRecordDialog.pageNum + 1)"
            >下一页</button>
          </footer>
        </section>
      </div>

      <div v-if="grainUseRecordDialog.visible" class="grain-use-record-layer" @click.self="closeGrainUseRecordDialog">
        <section
          class="grain-use-record-dialog"
          role="dialog"
          aria-modal="true"
          :aria-label="grainUseRecordDialog.title"
        >
          <header class="grain-use-record-dialog__header">
            <div>
              <span>粮食检疫监管数据消耗明细</span>
              <strong>{{ grainUseRecordDialog.title }}</strong>
            </div>
            <button
              type="button"
              class="grain-use-record-dialog__close"
              aria-label="关闭"
              @click="closeGrainUseRecordDialog"
            >×</button>
          </header>

          <div class="grain-use-record-table">
            <div class="grain-use-record-table__inner">
              <div class="grain-use-record-table__head">
                <span>发生日期</span>
                <span>报关单号</span>
                <span>加工企业</span>
                <span>品种</span>
                <span>当日加工重量（吨）</span>
                <span>加工方式</span>
                <span>当日下脚料（kg）</span>
                <span>已处理下脚料（kg）</span>
                <span>处理日期</span>
              </div>
              <div
                v-for="(use, index) in pagedGrainUseRecordRows"
                :key="`grain-use-record-${use.id}-${index}`"
                class="grain-use-record-table__row"
              >
                <span>{{ use.sentDate || '-' }}</span>
                <span class="grain-record-cell--wrap">{{ use.trackingNumber || '-' }}</span>
                <span class="grain-record-cell--wrap">{{ use.processingEnterprise || '-' }}</span>
                <span class="grain-record-cell--wrap">{{ use.variety || '-' }}</span>
                <span>{{ use.processingNum || '-' }}</span>
                <span class="grain-record-cell--wrap">{{ use.feedingMethod || '-' }}</span>
                <span>{{ use.offcutsNum || '-' }}</span>
                <span>{{ use.offcutsHandleNum || '-' }}</span>
                <span>{{ use.offcutsHandleDate || '-' }}</span>
              </div>
              <div v-if="!pagedGrainUseRecordRows.length" class="grain-detail-empty">暂无消耗记录</div>
            </div>
          </div>

          <footer class="grain-use-record-pagination">
            <span>共 {{ grainUseRecordDialog.rows.length }} 条</span>
            <button
              type="button"
              :disabled="grainUseRecordDialog.pageNum <= 1"
              @click="changeGrainUseRecordPage(grainUseRecordDialog.pageNum - 1)"
            >上一页</button>
            <strong>第 {{ grainUseRecordDialog.pageNum }} / {{ grainUseRecordTotalPages }} 页</strong>
            <button
              type="button"
              :disabled="grainUseRecordDialog.pageNum >= grainUseRecordTotalPages"
              @click="changeGrainUseRecordPage(grainUseRecordDialog.pageNum + 1)"
            >下一页</button>
          </footer>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import * as echarts from 'echarts'
import TabHeader from '@/components/common/TabHeader.vue'
import TanchuangFrame from '@/components/common/TanchuangFrame.vue'
import WlRegionDialog from './WlRegionDialog.vue'
import BondedRegionDialog from './BondedRegionDialog.vue'
import ScreenDateRangePicker from '@/components/common/ScreenDateRangePicker.vue'
import { getRecentDateValues } from '@/utils/dateRange'
import {
  getCenterWarningPage,
  getCenterGoodsTop,
  getCenterTopIcons,
  getCenterTopMaps,
  getCenterTopOnDuty,
  getCenterTradeRows,
  getRightDutyRows,
  getRightWorkRows,
  getLeftBarterChart,
  getLeftRailwayChart,
  getLeftTaxationChart,
  getRightGrainChart,
  getRightTransferSummary,
  getRightUseSummary
} from '@/api/datav'

const AUDIT_STYLE_MAP = {
  稽查: {
    color: '#27dcff',
    barColor: 'linear-gradient(90deg, #14d7d7 0%, #17e4d5 70%, #f2ffff 100%)',
    pendingGradient: 'linear-gradient(90deg, #0e88ff 0%, #27dcff 100%)',
    doneGradient: 'linear-gradient(90deg, #12d8c8 0%, #8efcff 100%)'
  },
  核查: {
    color: '#ffbf2b',
    barColor: 'linear-gradient(90deg, #16d9d5 0%, #14ead1 68%, #f3ffff 100%)',
    pendingGradient: 'linear-gradient(90deg, #1772ff 0%, #36c6ff 100%)',
    doneGradient: 'linear-gradient(90deg, #10d6b8 0%, #ffcf5b 100%)'
  },
  贸易调查: {
    color: '#ff5f56',
    barColor: 'linear-gradient(90deg, #10d0ca 0%, #15dfd1 68%, #ebffff 100%)',
    pendingGradient: 'linear-gradient(90deg, #245dff 0%, #5d91ff 100%)',
    doneGradient: 'linear-gradient(90deg, #12c6c3 0%, #ff7c72 100%)'
  },
  主动披露: {
    color: '#43e97b',
    barColor: 'linear-gradient(90deg, #13d4cf 0%, #18e7d6 68%, #efffff 100%)',
    pendingGradient: 'linear-gradient(90deg, #116cff 0%, #28cfff 100%)',
    doneGradient: 'linear-gradient(90deg, #16d3c9 0%, #43e97b 100%)'
  }
}

const DEFAULT_TRADE_COLOR = 'radial-gradient(circle at 34% 28%, rgba(111, 221, 255, 0.45), rgba(24, 126, 208, 0.26) 42%, rgba(7, 31, 80, 0.92) 100%)'
const WARNING_POLL_INTERVAL = 10000
const WARNING_PAGE_TYPES = {
  inspectionOver4Hours: 'four',
  clearanceEfficiency: 'time',
  inspectionException: 'abnormal',
  samplePendingResult: 'sampling'
}
const WARNING_READ_CACHE_KEY = 'WUCHANG_DATAV_WARNING_READ_STATE_V1'
const WARNING_READ_CACHE_LIMIT = 500

function createEmptyWarningReadState() {
  return {
    version: 1,
    tables: {}
  }
}

function normalizeWarningFingerprintList(value) {
  if (!Array.isArray(value)) {
    return []
  }
  return Array.from(new Set(value.map(item => String(item || '')).filter(Boolean))).slice(-WARNING_READ_CACHE_LIMIT)
}

function normalizeWarningSnapshot(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }
  return Object.keys(value).reduce((snapshot, fingerprint) => {
    const count = Number(value[fingerprint])
    if (Number.isFinite(count) && count > 0) {
      snapshot[fingerprint] = count
    }
    return snapshot
  }, {})
}

function normalizeWarningTableReadState(value) {
  const table = value && typeof value === 'object' ? value : {}
  return {
    queryKey: typeof table.queryKey === 'string' ? table.queryKey : '',
    snapshot: normalizeWarningSnapshot(table.snapshot),
    snapshotReady: Boolean(table.snapshotReady),
    read: normalizeWarningFingerprintList(table.read),
    unread: normalizeWarningFingerprintList(table.unread)
  }
}

function getWarningReadStorageKey() {
  let userKey = 'guest'
  try {
    const rawProfile = typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('WUCHANG_FRONT_USER')
      : ''
    const profile = rawProfile ? JSON.parse(rawProfile) : {}
    userKey = profile.userId || profile.userName || 'guest'
  } catch {
    userKey = 'guest'
  }
  return `${WARNING_READ_CACHE_KEY}:${String(userKey)}`
}

function loadWarningReadState() {
  const emptyState = createEmptyWarningReadState()
  if (typeof window === 'undefined' || !window.localStorage) {
    return emptyState
  }
  try {
    const rawState = window.localStorage.getItem(getWarningReadStorageKey())
    if (!rawState) {
      return emptyState
    }
    const parsedState = JSON.parse(rawState)
    const sourceTables = parsedState && parsedState.tables && typeof parsedState.tables === 'object'
      ? parsedState.tables
      : {}
    return {
      version: 1,
      tables: Object.keys(sourceTables).reduce((tables, key) => {
        tables[key] = normalizeWarningTableReadState(sourceTables[key])
        return tables
      }, {})
    }
  } catch {
    return emptyState
  }
}

function saveWarningReadState(state) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  try {
    window.localStorage.setItem(getWarningReadStorageKey(), JSON.stringify(state))
  } catch {
    // Ignore storage quota and privacy-mode errors; the dashboard can still work in memory.
  }
}

function mergeWarningFingerprints(current, additions) {
  return Array.from(new Set([
    ...(Array.isArray(current) ? current : []),
    ...(Array.isArray(additions) ? additions : [])
  ].map(item => String(item || '')).filter(Boolean))).slice(-WARNING_READ_CACHE_LIMIT)
}

export default {
  name: 'CenterPanel',
  props: {
    periodKey: {
      type: String,
      default: 'day'
    },
    dateRange: {
      type: Object,
      default: () => ({})
    }
  },
  components: { TabHeader, TanchuangFrame, WlRegionDialog, BondedRegionDialog, ScreenDateRangePicker },
  data() {
    return {
      activeMapMode: 'overview',
      selectedRegionName: '',
      failedMapNames: {},
      failedLegendNames: {},
      tradeBtns: ['进口', '出口'],
      tradeBtnIdx: 1,
      shortcutCards: [
        // { key: 'tax', label: '税收' },
        { key: 'bonded', label: '综保区' },
        { key: 'grain', label: '粮食检疫监管' },
        { key: 'trade', label: '外贸情况' },
        { key: 'audit', label: '稽核查作业' },
        { key: 'onduty', label: '在岗情况' }
        // { key: 'barter', label: '易货贸易' }
      ],
      activeShortcutKey: '',
      shortcutPeriodTabs: ['日数据', '月数据', '年数据'],
      shortcutPeriodKey: ['day', 'month', 'year'].includes(this.periodKey) ? this.periodKey : 'day',
      shortcutRequestId: 0,
      shortcutDateRanges: { trade: [], audit: [] },
      taxationChart: null,
      grainChart: null,
      grainViewTabs: [
        { key: 'statistics', label: '统计' },
        { key: 'offcuts', label: '下脚料' },
        { key: 'details', label: '明细' }
      ],
      grainViewIndex: 0,
      expandedGrainDateId: '',
      grainDetailPageNum: 1,
      grainDetailPageSize: 10,
      grainUseRecordDialog: {
        visible: false,
        title: '',
        rows: [],
        pageNum: 1,
        pageSize: 10
      },
      tianshanChart: null,
      barterChart: null,
      remoteTopOnDuty: null,
      remoteTopMaps: [],
      remoteTopIcons: [],
      remoteTradeRows: null,
      remoteGoodsTop: null,
      remoteTaxationChart: null,
      remoteRailwayChart: null,
      remoteBarterChart: null,
      remoteTransferSummary: null,
      remoteUseSummary: null,
      remoteGrainChart: null,
      remoteWarningRows: {},
      warningPages: Object.keys(WARNING_PAGE_TYPES).reduce((pages, key) => {
        pages[key] = { pageNum: 1, pageSize: 10, total: 0 }
        return pages
      }, {}),
      warningLoading: true,
      warningErrors: {},
      warningPollTimer: null,
      warningPollInFlight: false,
      warningRequestId: 0,
      warningReadState: loadWarningReadState(),
      warningNewFlags: {
        inspectionOver4Hours: false,
        clearanceEfficiency: false,
        inspectionException: false,
        samplePendingResult: false
      },
      remoteAuditRows: [],
      remoteOnDutyRows: [],
      expandedDutyDeptId: '',
      dutyRecordDialog: {
        visible: false,
        title: '',
        rows: [],
        pageNum: 1,
        pageSize: 8
      },
      showWlDialog: false,
      showBondedDialog: false,
      tradeRows: [],
      taxationFallbackData: {
        labels: [],
        legend: ['进口', '出口'],
        yAxisName: '万元',
        series: [
          { name: '进口', data: [] },
          { name: '出口', data: [] }
        ]
      },
      tianshanFallbackData: {
        labels: [],
        legend: ['天山号列数', '天山号标箱'],
        yAxisName: '列/个',
        series: [
          { name: '天山号列数', data: [] },
          { name: '天山号标箱', data: [] }
        ]
      },
      barterFallbackData: {
        labels: [],
        legend: ['易货贸易额进口', '易货贸易额出口', '票数', '易货贸易额'],
        yAxisName: '亿/票',
        series: [
          { name: '易货贸易额进口', type: 'bar', data: [] },
          { name: '易货贸易额出口', type: 'bar', data: [] },
          { name: '票数', type: 'line', data: [] },
          { name: '易货贸易额', type: 'bar', data: [] }
        ]
      },
      auditRows: [],
      tableTabs: ['查验超4小时', '通关时效分析', '查验异常数据', '已取样未出结果'],
      tableTabIdx: 0,
      warningTables: [
        {
          key: 'inspectionOver4Hours',
          minWidth: '100%',
          columns: [
            { key: 'id', label: '序号', width: '10%' },
            { key: 'date', label: '日期', width: '18%' },
            { key: 'declarationNo', label: '报关单号', width: '24%', wrap: true, small: true },
            { key: 'reason', label: '超四小时原因', width: '30%', align: 'left', wrap: true },
            { key: 'remark', label: '备注', width: '18%', align: 'left', wrap: true }
          ],
          rows: []
        },
        {
          key: 'clearanceEfficiency',
          minWidth: '100%',
          columns: [
            { key: 'id', label: '序号', width: '12%' },
            { key: 'date', label: '日期', width: '28%' },
            { key: 'importHours', label: '进口（小时）', width: '30%' },
            { key: 'exportHours', label: '出口（小时）', width: '30%' }
          ],
          rows: []
        },
        {
          key: 'inspectionException',
          minWidth: '100%',
          columns: [
            { key: 'id', label: '序号', width: '8%' },
            { key: 'date', label: '日期', width: '14%' },
            { key: 'declarationNo', label: '报关单号', width: '18%', wrap: true, small: true },
            { key: 'issue', label: '具体问题表现', width: '24%', align: 'left', wrap: true },
            { key: 'solution', label: '解决措施', width: '22%', align: 'left', wrap: true },
            { key: 'remark', label: '备注', width: '14%', align: 'left', wrap: true }
          ],
          rows: []
        },
        {
          key: 'samplePendingResult',
          minWidth: '100%',
          columns: [
            { key: 'id', label: '序号', width: '8%' },
            { key: 'sampleSent', label: '是否送样', width: '12%' },
            { key: 'sampleDate', label: '送样日期', width: '16%' },
            { key: 'declarationNo', label: '报关单号', width: '24%', wrap: true, small: true },
            { key: 'applicationNo', label: '申请单号', width: '24%', wrap: true, small: true },
            { key: 'remark', label: '备注', width: '16%', align: 'left', wrap: true }
          ],
          rows: []
        }
      ]
    }
  },
  computed: {
    resolvedTopData() {
      return {
        onDuty: this.remoteTopOnDuty,
        maps: this.remoteTopMaps,
        icons: this.remoteTopIcons
      }
    },
    resolvedDutyInfo() {
      const dutyInfo = this.resolvedTopData && this.resolvedTopData.onDuty
      return {
        leader: (dutyInfo && dutyInfo.leader) || '',
        employee: (dutyInfo && dutyInfo.employee) || ''
      }
    },
    displayRegions() {
      const remoteMaps = this.resolvedTopData && Array.isArray(this.resolvedTopData.maps)
        ? this.resolvedTopData.maps
        : []
      const current = this.currentRegionName
      return remoteMaps.map((item, index) => ({
        name: item.name,
        imageUrl: item.imageUrl || '',
        iconUrl: item.iconUrl ? this.normalizeAssetUrl(item.iconUrl) : '',
        biosecurityImageUrl: item.biosecurityImageUrl || '',
        regionDescription: item.regionDescription || '',
        showDescription: item.showDescription || '0',
        active: current ? item.name === current : index === 0
      }))
    },
    currentRegionName() {
      const remoteMaps = this.resolvedTopData && Array.isArray(this.resolvedTopData.maps)
        ? this.resolvedTopData.maps
        : []
      const names = remoteMaps.map(item => item.name)
      if (names.includes(this.selectedRegionName)) {
        return this.selectedRegionName
      }
      return names[0] || ''
    },
    currentTopMap() {
      return this.displayRegions.find(item => item.name === this.currentRegionName) || this.displayRegions[0] || null
    },
    currentRegionDescription() {
      return (this.currentTopMap && this.currentTopMap.regionDescription) || ''
    },
    shouldShowRegionDescription() {
      if (!this.currentTopMap) {
        return false
      }
      return ['1', 1, true, 'true'].includes(this.currentTopMap.showDescription) && Boolean(this.currentRegionDescription)
    },
    activeShortcut() {
      return this.shortcutCards.find(item => item.key === this.activeShortcutKey) || null
    },
    shortcutDialogTitle() {
      return this.activeShortcut ? this.activeShortcut.label : ''
    },
    isShortcutDateRange() {
      return ['trade', 'audit'].includes(this.activeShortcutKey)
    },
    shortcutPeriodIndex() {
      const index = ['day', 'month', 'year'].indexOf(this.shortcutPeriodKey)
      return index >= 0 ? index : 0
    },
    grainViewKey() {
      return (this.grainViewTabs[this.grainViewIndex] || this.grainViewTabs[0]).key
    },
    resolvedGrainDetailGroups() {
      const groups = this.remoteGrainChart && Array.isArray(this.remoteGrainChart.detailGroups)
        ? this.remoteGrainChart.detailGroups
        : []
      return groups.slice().sort((left, right) => {
        const leftDate = String((left && (left.date || left.label)) || '')
        const rightDate = String((right && (right.date || right.label)) || '')
        return rightDate > leftDate ? 1 : rightDate < leftDate ? -1 : 0
      })
    },
    grainDetailTotalPages() {
      return Math.max(Math.ceil(this.resolvedGrainDetailGroups.length / this.grainDetailPageSize), 1)
    },
    pagedGrainDetailGroups() {
      const start = (this.grainDetailPageNum - 1) * this.grainDetailPageSize
      return this.resolvedGrainDetailGroups.slice(start, start + this.grainDetailPageSize)
    },
    grainUseRecordTotalPages() {
      return Math.max(Math.ceil(this.grainUseRecordDialog.rows.length / this.grainUseRecordDialog.pageSize), 1)
    },
    pagedGrainUseRecordRows() {
      const start = (this.grainUseRecordDialog.pageNum - 1) * this.grainUseRecordDialog.pageSize
      return this.grainUseRecordDialog.rows.slice(start, start + this.grainUseRecordDialog.pageSize)
    },
    resolvedTradeRows() {
      return this.remoteTradeRows
    },
    displayTradeRows() {
      const modeKey = this.tradeBtnIdx === 0 ? 'import' : 'export'
      const rows = this.resolvedTradeRows && Array.isArray(this.resolvedTradeRows[modeKey])
        ? this.resolvedTradeRows[modeKey]
        : this.tradeRows

      return rows.map(item => ({
        ...item,
        color: this.normalizeTradeColor(item.color)
      }))
    },
    tradeBubbleNodes() {
      const rows = this.displayTradeRows.slice(0, 7)
      const angleSets = {
        1: [-90],
        2: [-150, 30],
        3: [-120, 0, 120],
        4: [-140, -40, 40, 140],
        5: [-145, -70, 0, 70, 145],
        6: [-150, -90, -30, 30, 90, 150],
        7: [-155, -105, -55, 0, 55, 105, 155]
      }
      const angles = angleSets[rows.length] || angleSets[7]
      return rows.map((item, index) => {
        const pct = Number(item.pct) || 0
        const angle = angles[index]
        const radian = angle * Math.PI / 180
        const left = 50 + Math.cos(radian) * 31
        const top = 51 + Math.sin(radian) * 34
        const distance = Math.sqrt(Math.pow(left - 50, 2) + Math.pow(top - 51, 2)) - 7.5
        const rotate = Math.atan2(top - 51, left - 50) * 180 / Math.PI
        return {
          key: `${item.region || 'region'}-${index}`,
          region: item.region || '',
          value: item.value || 0,
          pct,
          style: {
            left: `${left}%`,
            top: `${top}%`,
            width: 'clamp(108px, 6.15vw, 132px)',
            height: 'clamp(108px, 6.15vw, 132px)',
            background: item.color
          },
          lineStyle: {
            left: '50%',
            top: '51%',
            width: `${Math.max(14, distance)}%`,
            transform: `rotate(${rotate}deg)`
          }
        }
      })
    },
    resolvedGoodsTop() {
      const modeKey = this.tradeBtnIdx === 0 ? 'import' : 'export'
      if (this.remoteGoodsTop && Array.isArray(this.remoteGoodsTop[modeKey])) {
        return this.remoteGoodsTop[modeKey]
      }
      return []
    },
    tradeFunnelItems() {
      const colors = [
        'linear-gradient(180deg, rgba(47, 203, 255, 0.96), rgba(16, 126, 215, 0.98))',
        'linear-gradient(180deg, rgba(37, 176, 237, 0.96), rgba(16, 103, 198, 0.98))',
        'linear-gradient(180deg, rgba(50, 136, 255, 0.96), rgba(37, 83, 216, 0.98))',
        'linear-gradient(180deg, rgba(38, 218, 202, 0.94), rgba(18, 139, 173, 0.98))',
        'linear-gradient(180deg, rgba(96, 122, 255, 0.94), rgba(52, 77, 204, 0.98))'
      ]
      const widths = [96, 82, 68, 54, 40]
      return this.resolvedGoodsTop.slice(0, 5).map((item, index) => {
        const width = widths[index] || Math.max(36, 96 - index * 14)
        const nextWidth = widths[index + 1] || Math.max(30, width * 0.72)
        const bottomInset = Math.max(0, ((width - nextWidth) / width) * 50)
        return {
          key: `${item.name || 'goods'}-${index}`,
          name: item.name || '',
          value: item.value || 0,
          unit: '（亿）',
          pct: Number(item.pct) || 0,
          title: `${item.name || ''} ${item.value || 0} （亿）`,
          style: {
            width: `${width}%`,
            background: colors[index % colors.length],
            clipPath: `polygon(0 0, 100% 0, ${100 - bottomInset}% 100%, ${bottomInset}% 100%)`
          }
        }
      })
    },
    resolvedTaxationChart() {
      return this.remoteTaxationChart && Array.isArray(this.remoteTaxationChart.series)
        ? this.remoteTaxationChart
        : this.taxationFallbackData
    },
    resolvedTianshanChart() {
      const source = this.remoteRailwayChart && Array.isArray(this.remoteRailwayChart.series)
        ? this.remoteRailwayChart
        : this.tianshanFallbackData
      const names = ['天山号列数', '天山号标箱']
      return {
        labels: source.labels || [],
        legend: names,
        yAxisName: source.yAxisName || '列/个',
        series: names.map((name, index) => {
          const item = (source.series || []).find(seriesItem => seriesItem && seriesItem.name === name)
          return item || this.tianshanFallbackData.series[index]
        })
      }
    },
    resolvedBarterChart() {
      return this.remoteBarterChart && Array.isArray(this.remoteBarterChart.series)
        ? this.remoteBarterChart
        : this.barterFallbackData
    },
    mapImage() {
      const imageUrl = this.activeMapMode === 'biosecurity'
        ? ((this.currentTopMap && this.currentTopMap.biosecurityImageUrl) || (this.currentTopMap && this.currentTopMap.imageUrl))
        : (this.currentTopMap && this.currentTopMap.imageUrl)
      const failedKey = this.getMapImageFailedKey()
      if (!imageUrl || this.failedMapNames[failedKey]) {
        return ''
      }
      return this.normalizeAssetUrl(imageUrl)
    },
    displayLegends() {
      const remoteIcons = this.resolvedTopData && Array.isArray(this.resolvedTopData.icons)
        ? this.resolvedTopData.icons
        : []
      return remoteIcons
        .filter(item => item.showStatus !== '0')
        .map(item => ({
        name: item.name,
        iconUrl: this.isColorValue(item.iconUrl) || this.failedLegendNames[item.name]
          ? ''
          : this.normalizeAssetUrl(item.iconUrl),
        color: item.color || '#55d2ff'
        }))
    },
    resolvedWarningTables() {
      return this.warningTables.map(table => ({
        ...table,
        rows: Array.isArray(this.remoteWarningRows[table.key]) ? this.remoteWarningRows[table.key] : table.rows
      }))
    },
    currentWarningTable() {
      return this.resolvedWarningTables[this.tableTabIdx] || this.resolvedWarningTables[0]
    },
    currentWarningPage() {
      return this.warningPages[this.currentWarningTable.key]
    },
    resolvedAuditCards() {
      const rows = Array.isArray(this.remoteAuditRows) && this.remoteAuditRows.length ? this.remoteAuditRows : this.auditRows
      return rows.map(item => {
        const pendingCount = Number(item.pendingCount) || 0
        const doneCount = Number(item.doneCount) || 0
        const total = pendingCount + doneCount
        const style = AUDIT_STYLE_MAP[item.label] || AUDIT_STYLE_MAP['主动披露']
        return {
          ...item,
          color: style.color,
          pendingCount,
          doneCount,
          total,
          pendingPct: total > 0 ? Math.max(4, Math.round((pendingCount / total) * 100)) : 0,
          donePct: total > 0 ? Math.max(4, Math.round((doneCount / total) * 100)) : 0,
          pendingGradient: style.pendingGradient || style.barColor,
          doneGradient: style.doneGradient || 'linear-gradient(90deg, #2384ff 0%, #42d9ff 100%)'
        }
      })
    },
    resolvedOnDutyRows() {
      return Array.isArray(this.remoteOnDutyRows)
        ? this.remoteOnDutyRows.filter(item => item && !['乌拉斯台', '乌剌斯太'].includes(item.region))
        : []
    },
    isDutyDailyPeriod() {
      return this.shortcutPeriodKey === 'day'
    },
    dutyRecordTotalPages() {
      return Math.max(Math.ceil(this.dutyRecordDialog.rows.length / this.dutyRecordDialog.pageSize), 1)
    },
    pagedDutyRecordRows() {
      const start = (this.dutyRecordDialog.pageNum - 1) * this.dutyRecordDialog.pageSize
      return this.dutyRecordDialog.rows.slice(start, start + this.dutyRecordDialog.pageSize)
    },
    auditSummary() {
      return this.resolvedAuditCards.reduce((summary, item) => {
        summary.pending += item.pendingCount
        summary.done += item.doneCount
        summary.total += item.total
        return summary
      }, { pending: 0, done: 0, total: 0 })
    }
  },
  watch: {
    dateRange: {
      deep: true,
      async handler() {
        Object.values(this.warningPages).forEach(page => { page.pageNum = 1 })
        this.remoteWarningRows = {}
        const requests = [this.fetchPeriodData(true)]
        if (this.activeShortcutKey && !this.isShortcutDateRange) {
          requests.push(this.fetchShortcutData())
        }
        await Promise.all(requests)
      }
    }
  },
  async mounted() {
    // 预警数据需要独立于顶部地图等慢接口，初始化时立即拉取四张表。
    this.startWarningPolling()
    this.fetchStaticData()
    this.syncRegionSelection()
  },
  beforeDestroy() {
    this.stopWarningPolling()
    this.disposeTaxationChart()
    this.disposeGrainChart()
    this.disposeTianshanChart()
    this.disposeBarterChart()
  },
  methods: {
    normalizeTradeColor(color) {
      const value = String(color || '').trim()
      if (value) {
        return value
      }
      return DEFAULT_TRADE_COLOR
    },
    switchMapMode(mode) {
      this.activeMapMode = mode
    },
    switchRegion(activeIndex) {
      const target = this.displayRegions[activeIndex]
      if (target) {
        this.selectedRegionName = target.name
        if (this.isWlRegion(target.name)) {
          this.openWlDialog()
        }
      }
    },
    isWlRegion(name) {
      const value = String(name || '')
      return value.includes('乌拉斯台') || value.includes('乌剌斯台') || value.includes('乌剌斯太')
    },
    openWlDialog() {
      this.showWlDialog = true
    },
    closeWlDialog() {
      this.showWlDialog = false
    },
    closeBondedDialog() {
      this.showBondedDialog = false
    },
    openShortcutDialog(item) {
      const nextKey = item && item.key ? item.key : ''
      if (nextKey === 'bonded') {
        this.closeShortcutDialog()
        this.showBondedDialog = true
        return
      }
      this.showBondedDialog = false
      if (this.activeShortcutKey === 'tax' && nextKey !== 'tax') {
        this.disposeTaxationChart()
      }
      if (this.activeShortcutKey === 'tianshan' && nextKey !== 'tianshan') {
        this.disposeTianshanChart()
      }
      if (this.activeShortcutKey === 'barter' && nextKey !== 'barter') {
        this.disposeBarterChart()
      }
      if (this.activeShortcutKey === 'grain' && nextKey !== 'grain') {
        this.disposeGrainChart()
      }
      if (nextKey === 'grain') {
        this.grainViewIndex = 0
        this.resetGrainDrilldown()
      }
      this.shortcutPeriodKey = this.normalizePeriod(this.periodKey)
      this.resetDutyDrilldown()
      this.activeShortcutKey = nextKey
      if (this.isShortcutDateRange && this.shortcutDateRanges[nextKey].length !== 2) {
        const { startDate, endDate } = this.dateRange || {}
        this.$set(this.shortcutDateRanges, nextKey, startDate && endDate
          ? [startDate, endDate]
          : getRecentDateValues(1))
      }
      this.fetchShortcutData()
      this.$nextTick(() => this.refreshShortcutChart())
    },
    closeShortcutDialog() {
      this.shortcutRequestId++
      this.showBondedDialog = false
      this.disposeTaxationChart()
      this.disposeGrainChart()
      this.disposeTianshanChart()
      this.disposeBarterChart()
      this.resetDutyDrilldown()
      this.grainViewIndex = 0
      this.resetGrainDrilldown()
      this.activeShortcutKey = ''
    },
    normalizePeriod(value) {
      return ['day', 'month', 'year'].includes(value) ? value : 'day'
    },
    switchShortcutPeriod(index) {
      const nextPeriod = ['day', 'month', 'year'][index] || 'day'
      if (nextPeriod === this.shortcutPeriodKey) {
        return
      }
      this.shortcutPeriodKey = nextPeriod
      this.resetDutyDrilldown()
      this.resetGrainDrilldown()
      this.fetchShortcutData()
    },
    switchGrainView(index) {
      if (index === this.grainViewIndex) {
        return
      }
      this.grainViewIndex = index
      this.resetGrainDrilldown()
      if (this.grainViewKey === 'details') {
        this.disposeGrainChart()
        return
      }
      this.$nextTick(() => this.initGrainChart())
    },
    getGrainDateId(item) {
      return String((item && (item.id || item.date)) || '')
    },
    isGrainDateExpanded(item) {
      return this.expandedGrainDateId === this.getGrainDateId(item)
    },
    toggleGrainDate(item) {
      const dateId = this.getGrainDateId(item)
      if (this.expandedGrainDateId === dateId) {
        this.expandedGrainDateId = ''
        return
      }
      this.expandedGrainDateId = dateId
    },
    openGrainUseRecordDialog(group, transfer) {
      const rows = Array.isArray(transfer && transfer.uses) ? transfer.uses : []
      if (!rows.length) {
        return
      }
      const date = (group && (group.date || group.label)) || (transfer && transfer.sentDate) || ''
      const reference = (transfer && (transfer.trackingNumber || transfer.transferEnterprise)) || '未关联调运单'
      this.grainUseRecordDialog = {
        ...this.grainUseRecordDialog,
        visible: true,
        title: `${date} · ${reference}`,
        rows,
        pageNum: 1
      }
    },
    closeGrainUseRecordDialog() {
      this.grainUseRecordDialog = {
        ...this.grainUseRecordDialog,
        visible: false,
        title: '',
        rows: [],
        pageNum: 1
      }
    },
    changeGrainUseRecordPage(pageNum) {
      const nextPage = Math.max(1, Math.min(pageNum, this.grainUseRecordTotalPages))
      this.grainUseRecordDialog.pageNum = nextPage
    },
    changeGrainDetailPage(pageNum) {
      const nextPage = Math.max(1, Math.min(pageNum, this.grainDetailTotalPages))
      this.grainDetailPageNum = nextPage
      this.expandedGrainDateId = ''
    },
    resetGrainDrilldown() {
      this.expandedGrainDateId = ''
      this.grainDetailPageNum = 1
      this.closeGrainUseRecordDialog()
    },
    formatGrainNumber(value) {
      const numeric = Number(value)
      if (!Number.isFinite(numeric)) {
        return '0'
      }
      return numeric.toFixed(2).replace(/\.?0+$/, '')
    },
    getDutyDepartmentId(item) {
      return String((item && (item.id || item.dept)) || '')
    },
    isDutyDepartmentExpanded(item) {
      return this.expandedDutyDeptId === this.getDutyDepartmentId(item)
    },
    toggleDutyDepartment(item) {
      const departmentId = this.getDutyDepartmentId(item)
      this.expandedDutyDeptId = this.expandedDutyDeptId === departmentId ? '' : departmentId
    },
    openDutyRecordDialog(department, person) {
      this.dutyRecordDialog = {
        ...this.dutyRecordDialog,
        visible: true,
        title: `${department.dept || ''} · ${person.name || '未填写姓名'}`,
        rows: Array.isArray(person.records) ? person.records : [],
        pageNum: 1
      }
    },
    closeDutyRecordDialog() {
      this.dutyRecordDialog = {
        ...this.dutyRecordDialog,
        visible: false,
        title: '',
        rows: [],
        pageNum: 1
      }
    },
    changeDutyRecordPage(pageNum) {
      const nextPage = Math.max(1, Math.min(pageNum, this.dutyRecordTotalPages))
      this.dutyRecordDialog.pageNum = nextPage
    },
    resetDutyDrilldown() {
      this.expandedDutyDeptId = ''
      this.closeDutyRecordDialog()
    },
    getDutyStatusClass(status) {
      if (['在岗', '外勤执法', '支援口岸'].includes(status)) return 'duty-status duty-status--active'
      if (status === '外勤') return 'duty-status duty-status--out'
      return 'duty-status duty-status--leave'
    },
    refreshShortcutChart() {
      if (this.activeShortcutKey === 'tax') {
        this.initTaxationChart()
      }
      if (this.activeShortcutKey === 'tianshan') {
        this.initTianshanChart()
      }
      if (this.activeShortcutKey === 'barter') {
        this.initBarterChart()
      }
      if (this.activeShortcutKey === 'grain' && this.grainViewKey !== 'details') {
        this.initGrainChart()
      }
    },
    initTaxationChart() {
      if (!this.$refs.taxationChart) {
        return
      }
      if (!this.taxationChart) {
        this.taxationChart = echarts.init(this.$refs.taxationChart)
      }
      this.updateTaxationChart()
    },
    disposeTaxationChart() {
      if (this.taxationChart) {
        this.taxationChart.dispose()
        this.taxationChart = null
      }
    },
    initGrainChart() {
      if (!this.$refs.grainChart) {
        return
      }
      if (!this.grainChart) {
        this.grainChart = echarts.init(this.$refs.grainChart)
      }
      this.updateGrainChart()
    },
    disposeGrainChart() {
      if (this.grainChart) {
        this.grainChart.dispose()
        this.grainChart = null
      }
    },
    updateGrainChart() {
      const chart = this.grainChart
      if (!chart) {
        return
      }

      const currentData = this.remoteGrainChart || { labels: [], series: [] }
      const labels = Array.isArray(currentData.labels) ? currentData.labels : []
      const series = Array.isArray(currentData.series) ? currentData.series : []
      const findSeries = name => series.find(item => item && item.name === name) || { data: [] }
      const transferSeries = findSeries('调运重量')
      const actualTransferSeries = findSeries('实际调入重量')
      const processingSeries = findSeries('当日加工重量')
      const offcutsSeries = findSeries('当日下脚料重量')
      const processingLabel = '加工重量'
      const offcutsLabel = '下脚料重量'
      const getSeriesData = item => Array.isArray(item.data) ? item.data : []
      const actualTransferData = getSeriesData(actualTransferSeries)
      const processingData = getSeriesData(processingSeries)
      const offcutsView = this.grainViewKey === 'offcuts'
      const formatGrainNumber = value => {
        const numeric = Number(value)
        if (!Number.isFinite(numeric)) {
          return '0'
        }
        return numeric.toFixed(2).replace(/\.?0+$/, '')
      }
      const buildNestedBarSeries = (name, source, width, offset, colors, z) => ({
        name,
        type: 'custom',
        coordinateSystem: 'cartesian2d',
        data: getSeriesData(source).map((value, index) => [index, Number(value) || 0]),
        encode: { x: 0, y: 1 },
        renderItem(renderParams, api) {
          const categoryIndex = api.value(0)
          const value = Number(api.value(1)) || 0
          const base = api.coord([categoryIndex, 0])
          const top = api.coord([categoryIndex, value])
          const topY = Math.min(base[1], top[1])
          const height = Math.abs(base[1] - top[1])
          return {
            type: 'rect',
            shape: {
              x: base[0] - width / 2 + offset,
              y: topY,
              width,
              height,
              r: [4, 4, 0, 0]
            },
            style: api.style()
          }
        },
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: colors[0] },
            { offset: 1, color: colors[1] }
          ])
        },
        z
      })

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(3, 17, 45, 0.94)',
          borderColor: 'rgba(68, 180, 255, 0.34)',
          borderWidth: 1,
          padding: [10, 14],
          textStyle: { color: '#dff6ff', fontSize: 14 },
          axisPointer: offcutsView
            ? { type: 'line', lineStyle: { color: 'rgba(255, 126, 181, 0.46)', type: 'dashed' } }
            : { type: 'shadow', shadowStyle: { color: 'rgba(80, 138, 255, 0.08)' } },
          formatter(params) {
            const title = params.length ? params[0].axisValueLabel : ''
            const dataIndex = params.length ? params[0].dataIndex : -1
            if (dataIndex < 0) {
              return title
            }

            if (offcutsView) {
              const marker = params[0] && params[0].marker ? params[0].marker : ''
              return `${title}<br/>${marker}${offcutsLabel}：${formatGrainNumber(getSeriesData(offcutsSeries)[dataIndex])}kg`
            }

            const markerFor = name => {
              const item = params.find(entry => entry.seriesName === name)
              return item ? item.marker : ''
            }
            const actualTransfer = Number(actualTransferData[dataIndex] || 0)
            const processed = Number(processingData[dataIndex] || 0)
            const unprocessed = Math.max(0, actualTransfer - processed)
            const rows = [
              `${markerFor('调运重量')}调运重量：${formatGrainNumber(getSeriesData(transferSeries)[dataIndex])}吨`,
              `${markerFor('实际调入重量')}实际调入重量：${formatGrainNumber(actualTransfer)}吨`,
              `<span style="display:inline-block;width:10px;height:10px;margin-right:5px;border-radius:50%;background:#9ed5ff;"></span>未加工量：${formatGrainNumber(unprocessed)}吨`,
              `${markerFor(processingLabel)}${processingLabel}：${formatGrainNumber(processed)}吨`
            ]
            return [title, ...rows].join('<br/>')
          }
        },
        legend: {
          data: offcutsView ? [offcutsLabel] : ['调运重量', '实际调入重量', processingLabel],
          top: 12,
          left: 'center',
          itemWidth: 14,
          itemHeight: 10,
          itemGap: 24,
          textStyle: { color: '#c5e9ff', fontSize: 13 }
        },
        grid: { left: 70, right: 74, top: 66, bottom: 38 },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: 'rgba(122, 203, 255, 0.38)' } },
          axisTick: { show: false },
          axisLabel: { color: '#c7edff', fontSize: 14, margin: 14 }
        },
        yAxis: offcutsView
          ? [{
            type: 'value',
            name: '下脚料（kg）',
            nameTextStyle: { color: '#ff7fb4', fontSize: 12, padding: [0, -4, 6, 0] },
            splitLine: { lineStyle: { color: 'rgba(255, 104, 167, 0.12)', type: 'dashed' } },
            axisLine: { show: true, lineStyle: { color: 'rgba(255, 104, 167, 0.56)' } },
            axisLabel: { color: '#ffc0d9', fontSize: 12 }
          }]
          : [{
            type: 'value',
            name: '重量（吨）',
            nameTextStyle: { color: '#77d9ff', fontSize: 12, padding: [0, 0, 6, -4] },
            splitLine: { lineStyle: { color: 'rgba(82, 165, 255, 0.15)', type: 'dashed' } },
            axisLine: { show: true, lineStyle: { color: 'rgba(114, 222, 254, 0.44)' } },
            axisLabel: { color: '#9ed5ff', fontSize: 12 }
          }],
        series: offcutsView
          ? [{
            name: offcutsLabel,
            type: 'line',
            data: getSeriesData(offcutsSeries),
            symbol: 'circle',
            symbolSize: 9,
            lineStyle: { width: 3, color: '#ff68a7' },
            itemStyle: { color: '#ff68a7', borderColor: '#ffd7e8', borderWidth: 3 },
            z: 5
          }]
          : [
            buildNestedBarSeries('调运重量', transferSeries, 34, -7, ['#39bfff', '#1769d2'], 1),
            buildNestedBarSeries('实际调入重量', actualTransferSeries, 26, 0, ['#55f0c8', '#0fa889'], 2),
            buildNestedBarSeries(processingLabel, processingSeries, 18, 7, ['#ffd36a', '#d08b1a'], 3)
          ]
      }, { notMerge: true })
      chart.resize()
    },
    initTianshanChart() {
      if (!this.$refs.tianshanChart) {
        return
      }
      if (!this.tianshanChart) {
        this.tianshanChart = echarts.init(this.$refs.tianshanChart)
      }
      this.updateTianshanChart()
    },
    disposeTianshanChart() {
      if (this.tianshanChart) {
        this.tianshanChart.dispose()
        this.tianshanChart = null
      }
    },
    updateTianshanChart() {
      const chart = this.tianshanChart
      const currentData = this.resolvedTianshanChart
      if (!chart || !currentData) {
        return
      }

      const trainSeries = (currentData.series || []).find(item => item && item.name === '天山号列数') || { name: '天山号列数', data: [] }
      const containerSeries = (currentData.series || []).find(item => item && item.name === '天山号标箱') || { name: '天山号标箱', data: [] }
      chart.setOption(
        {
          tooltip: {
            ...this.getTaxationTooltipConfig(),
            axisPointer: {
              type: 'line',
              lineStyle: {
                color: 'rgba(210, 233, 255, 0.52)',
                type: 'dashed'
              }
            }
          },
          legend: {
            ...this.getTaxationLegendConfig(currentData.legend || ['天山号列数', '天山号标箱']),
            data: [
              { name: '天山号列数', icon: 'rect' },
              { name: '天山号标箱', icon: 'rect' }
            ]
          },
          grid: {
            left: 54,
            right: 58,
            top: 58,
            bottom: 34
          },
          xAxis: {
            type: 'category',
            data: currentData.labels || [],
            axisLine: { lineStyle: { color: 'rgba(122, 203, 255, 0.34)' } },
            axisTick: { show: false },
            axisLabel: {
              color: '#bde8ff',
              fontSize: 12,
              margin: 12
            }
          },
          yAxis: [
            {
              type: 'value',
              name: '列数（列）',
              minInterval: 1,
              nameTextStyle: {
                color: '#72DEFE',
                fontSize: 12,
                padding: [0, 0, 4, -2]
              },
              splitLine: { lineStyle: { color: 'rgba(82, 165, 255, 0.14)', type: 'dashed' } },
              axisLine: { show: true, lineStyle: { color: 'rgba(114, 222, 254, 0.44)' } },
              axisLabel: { color: '#9ed5ff', fontSize: 12 }
            },
            {
              type: 'value',
              name: '标箱（个）',
              minInterval: 1,
              nameTextStyle: {
                color: '#FF9A1A',
                fontSize: 12,
                padding: [0, 0, 4, 6]
              },
              splitLine: { show: false },
              axisLine: { show: true, lineStyle: { color: 'rgba(255, 154, 26, 0.62)' } },
              axisLabel: { color: '#ffd19a', fontSize: 12 }
            }
          ],
          series: [
            this.createTianshanBarSeries(trainSeries.name, trainSeries.data, '#1584DA', '#72DEFE', 0),
            this.createTianshanLineSeries(containerSeries.name, containerSeries.data, '#FF9A1A', 1)
          ]
        },
        { notMerge: true }
      )
      chart.resize()
    },
    createTianshanBarSeries(name, data, startColor, endColor, yAxisIndex) {
      return {
        name,
        type: 'bar',
        yAxisIndex,
        barWidth: 12,
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: endColor },
            { offset: 1, color: startColor }
          ])
        },
        emphasis: {
          focus: 'series'
        },
        data: data || []
      }
    },
    createTianshanLineSeries(name, data, color, yAxisIndex) {
      return {
        name,
        type: 'line',
        yAxisIndex,
        smooth: true,
        symbol: 'circle',
        symbolSize: 7,
        lineStyle: {
          width: 3,
          color
        },
        itemStyle: {
          color,
          borderColor: color,
          borderWidth: 3
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: `${color}36` },
            { offset: 1, color: `${color}04` }
          ])
        },
        emphasis: {
          focus: 'series'
        },
        data: data || []
      }
    },
    updateTaxationChart() {
      const chart = this.taxationChart
      const currentData = this.resolvedTaxationChart
      if (!chart || !currentData) {
        return
      }

      chart.setOption(
        {
          tooltip: this.getTaxationTooltipConfig(),
          legend: this.getTaxationLegendConfig(currentData.legend || ['进口', '出口']),
          grid: {
            left: 42,
            right: 18,
            top: 58,
            bottom: 26
          },
          xAxis: {
            type: 'category',
            data: currentData.labels || [],
            axisLine: { lineStyle: { color: 'rgba(122, 203, 255, 0.34)' } },
            axisTick: { show: false },
            axisLabel: {
              color: '#bde8ff',
              fontSize: 12,
              margin: 12
            }
          },
          yAxis: {
            type: 'value',
            name: currentData.yAxisName || '万元',
            nameTextStyle: {
              color: '#bde8ff',
              fontSize: 12,
              padding: [0, 0, 4, -2]
            },
            splitLine: { lineStyle: { color: 'rgba(82, 165, 255, 0.14)', type: 'dashed' } },
            axisLabel: { color: '#9ed5ff', fontSize: 12 }
          },
          series: (currentData.series || []).map((item, index) => ({
            ...this.createTaxationBarSeries(item.name, item.data, index === 0 ? ['#1584DA', '#72DEFE'] : ['#DA4D15', '#FED572']),
            barMaxWidth: 38,
            barCategoryGap: '12%',
            barGap: '16%'
          }))
        },
        { notMerge: true }
      )
      chart.resize()
    },
    createTaxationBarSeries(name, data, colors) {
      return {
        name,
        type: 'bar',
        barWidth: 30,
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: colors[1] },
            { offset: 1, color: colors[0] }
          ])
        },
        emphasis: {
          focus: 'series'
        },
        data
      }
    },
    getTaxationTooltipConfig() {
      return {
        trigger: 'axis',
        backgroundColor: 'rgba(3, 17, 45, 0.9)',
        borderColor: 'rgba(68, 180, 255, 0.25)',
        borderWidth: 1,
        padding: [10, 14],
        textStyle: {
          color: '#dff6ff',
          fontSize: 14
        },
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: 'rgba(80, 138, 255, 0.08)'
          }
        }
      }
    },
    getTaxationLegendConfig(data) {
      return {
        data,
        top: 10,
        left: 'center',
        icon: 'rect',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 16,
        textStyle: {
          color: '#c5e9ff',
          fontSize: 13
        }
      }
    },
    initBarterChart() {
      if (!this.$refs.barterChart) {
        return
      }
      if (!this.barterChart) {
        this.barterChart = echarts.init(this.$refs.barterChart)
      }
      this.updateBarterChart()
    },
    disposeBarterChart() {
      if (this.barterChart) {
        this.barterChart.dispose()
        this.barterChart = null
      }
    },
    updateBarterChart() {
      const chart = this.barterChart
      const currentData = this.resolvedBarterChart
      if (!chart || !currentData) {
        return
      }

      chart.setOption(this.buildBarterOption(currentData), { notMerge: true })
      chart.resize()
    },
    buildBarterOption(config) {
      return {
        tooltip: this.getTaxationTooltipConfig(),
        legend: {
          data: config.legend || [],
          top: 14,
          left: 42,
          right: 12,
          icon: 'rect',
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 12,
          textStyle: {
            color: '#c5e9ff',
            fontSize: 12
          }
        },
        grid: {
          left: 46,
          right: 18,
          top: 74,
          bottom: 26
        },
        xAxis: {
          type: 'category',
          data: config.labels || [],
          axisLine: { lineStyle: { color: 'rgba(122, 203, 255, 0.34)' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#bde8ff',
            fontSize: 12,
            margin: 12
          }
        },
        yAxis: {
          type: 'value',
          name: config.yAxisName || '亿/票',
          minInterval: 1,
          nameTextStyle: {
            color: '#bde8ff',
            fontSize: 12,
            padding: [0, 0, 8, -2]
          },
          splitLine: { lineStyle: { color: 'rgba(82, 165, 255, 0.14)', type: 'dashed' } },
          axisLabel: { color: '#9ed5ff', fontSize: 12 }
        },
        series: this.buildBarterSeries(config.series)
      }
    },
    buildBarterSeries(series) {
      const palettes = [
        ['#1584DA', '#72DEFE'],
        ['#DA4D15', '#FED572'],
        ['#50BC1F', '#8FF478'],
        ['#1584DA', '#72DEFE']
      ]
      let barIndex = 0
      return (series || []).map(item => {
        if (item.type === 'line') {
          return {
            name: item.name,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: {
              width: 3,
              color: '#FF7B1C'
            },
            itemStyle: {
              color: '#ffffff',
              borderWidth: 3,
              borderColor: '#FF7B1C'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(254, 213, 114, 0.32)' },
                { offset: 1, color: 'rgba(218, 77, 21, 0.02)' }
              ])
            },
            data: item.data || []
          }
        }

        const colors = palettes[barIndex % palettes.length]
        barIndex += 1
        return this.createTaxationBarSeries(item.name, item.data, colors)
      })
    },
    syncRegionSelection() {
      if (!this.selectedRegionName && this.displayRegions.length) {
        this.selectedRegionName = this.displayRegions[0].name
      }
      if (this.displayRegions.length && !this.displayRegions.some(item => item.name === this.selectedRegionName)) {
        this.selectedRegionName = this.displayRegions[0].name
      }
    },
    normalizeAssetUrl(url) {
      if (!url) return ''
      if (/^(https?:)?\/\//.test(url)) {
        return url
      }
      const normalized = url.startsWith('/') ? url : `/${url.replace(/^\/+/, '')}`
      if (normalized.startsWith('/prod-api/')) {
        return normalized
      }
      if (normalized.startsWith('/uploads/') || normalized.startsWith('/profile/')) {
        return `/prod-api${normalized}`
      }
      return normalized
    },
    isColorValue(value) {
      return Boolean(value) && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)
    },
    getMapImageFailedKey() {
      return `${this.activeMapMode}:${this.currentRegionName || '__empty_region__'}`
    },
    handleMapImageError() {
      const failedKey = this.getMapImageFailedKey()
      if (failedKey) {
        this.$set(this.failedMapNames, failedKey, true)
      }
    },
    handleLegendIconError(name) {
      if (name) {
        this.$set(this.failedLegendNames, name, true)
      }
    },
    getLegendMarkerStyle(item) {
      return {
        background: item.color || '#55d2ff'
      }
    },
    getWarningColumnClass(column) {
      return [
        column.align ? `info-table-col--${column.align}` : '',
        column.wrap ? 'info-table-col--wrap' : '',
        column.small ? 'info-table-col--small' : ''
      ]
    },
    getWarningColumnStyle(column) {
      return column.width ? { width: column.width } : {}
    },
    selectWarningTab(index) {
      this.tableTabIdx = index
      const table = this.warningTables[index]
      if (table) {
        this.markWarningTableRead(table.key)
      }
      this.resetWarningScroll()
    },
    resetWarningScroll() {
      this.$nextTick(() => {
        if (this.$refs.warningTableWrap) {
          this.$refs.warningTableWrap.scrollTop = 0
        }
      })
    },
    changeWarningPage(pageNum) {
      const page = this.currentWarningPage
      if (pageNum === page.pageNum) return
      page.pageNum = pageNum
      this.$set(this.remoteWarningRows, this.currentWarningTable.key, [])
      this.resetWarningScroll()
      this.fetchPeriodData(true)
    },
    ensureWarningReadTableState(tableKey) {
      if (!this.warningReadState.tables[tableKey]) {
        this.$set(this.warningReadState.tables, tableKey, {
          queryKey: '',
          snapshot: {},
          snapshotReady: false,
          read: [],
          unread: []
        })
      }
      return this.warningReadState.tables[tableKey]
    },
    getWarningQueryKey(tableKey) {
      const dateRange = this.dateRange || {}
      const page = this.warningPages[tableKey]
      return JSON.stringify({
        startDate: dateRange.startDate || '',
        endDate: dateRange.endDate || '',
        pageNum: page.pageNum,
        pageSize: page.pageSize
      })
    },
    markWarningTableRead(tableKey) {
      const rows = this.remoteWarningRows[tableKey]
      if (!Array.isArray(rows) || !rows.length) {
        return
      }
      const fingerprints = Object.keys(this.createWarningRowSnapshot(rows))
      if (!fingerprints.length) {
        return
      }
      const tableState = this.ensureWarningReadTableState(tableKey)
      const currentSet = new Set(fingerprints)
      tableState.read = mergeWarningFingerprints(tableState.read, fingerprints)
      tableState.unread = tableState.unread.filter(fingerprint => !currentSet.has(fingerprint))
      tableState.queryKey = this.getWarningQueryKey(tableKey)
      tableState.snapshot = this.createWarningRowSnapshot(rows)
      tableState.snapshotReady = true
      this.$set(this.warningNewFlags, tableKey, false)
      saveWarningReadState(this.warningReadState)
    },
    startWarningPolling() {
      this.stopWarningPolling()
      this.pollWarningRows()
      this.warningPollTimer = window.setInterval(() => {
        this.pollWarningRows()
      }, WARNING_POLL_INTERVAL)
    },
    stopWarningPolling() {
      if (this.warningPollTimer) {
        window.clearInterval(this.warningPollTimer)
        this.warningPollTimer = null
      }
    },
    async pollWarningRows() {
      if (this.warningPollInFlight) {
        return
      }
      this.warningPollInFlight = true
      try {
        await this.fetchPeriodData()
      } finally {
        this.warningPollInFlight = false
      }
    },
    createWarningRowSnapshot(rows) {
      return (Array.isArray(rows) ? rows : []).reduce((snapshot, row) => {
        const normalizedRow = Object.keys(row || {})
          .filter(key => key !== 'id')
          .sort()
          .reduce((result, key) => {
            result[key] = row[key]
            return result
          }, {})
        const fingerprint = JSON.stringify(normalizedRow)
        snapshot[fingerprint] = (snapshot[fingerprint] || 0) + 1
        return snapshot
      }, {})
    },
    hasNewWarningRows(previousSnapshot, nextSnapshot) {
      return Object.keys(nextSnapshot).some(fingerprint => (
        nextSnapshot[fingerprint] > (previousSnapshot[fingerprint] || 0)
      ))
    },
    applyWarningRows(nextRows) {
      let cacheChanged = false
      this.warningTables.forEach(table => {
        const queryKey = this.getWarningQueryKey(table.key)
        const rows = Array.isArray(nextRows[table.key]) ? nextRows[table.key] : []
        const nextSnapshot = this.createWarningRowSnapshot(rows)
        const tableState = this.ensureWarningReadTableState(table.key)
        const hasComparableSnapshot = tableState.snapshotReady && tableState.queryKey === queryKey

        if (hasComparableSnapshot && this.hasNewWarningRows(tableState.snapshot, nextSnapshot)) {
          const readSet = new Set(tableState.read)
          const newFingerprints = Object.keys(nextSnapshot).filter(fingerprint => (
            nextSnapshot[fingerprint] > (tableState.snapshot[fingerprint] || 0)
              && !readSet.has(fingerprint)
          ))
          if (newFingerprints.length) {
            tableState.unread = mergeWarningFingerprints(tableState.unread, newFingerprints)
            cacheChanged = true
          }
        }

        const readSet = new Set(tableState.read)
        const filteredUnread = tableState.unread.filter(fingerprint => !readSet.has(fingerprint))
        if (filteredUnread.length !== tableState.unread.length) {
          tableState.unread = filteredUnread
          cacheChanged = true
        }
        if (
          tableState.queryKey !== queryKey
          || !tableState.snapshotReady
          || JSON.stringify(tableState.snapshot) !== JSON.stringify(nextSnapshot)
        ) {
          cacheChanged = true
        }
        tableState.queryKey = queryKey
        tableState.snapshot = nextSnapshot
        tableState.snapshotReady = true
        const unreadSet = new Set(tableState.unread)
        this.$set(this.warningNewFlags, table.key, Object.keys(nextSnapshot).some(fingerprint => unreadSet.has(fingerprint)))
      })
      if (cacheChanged) {
        saveWarningReadState(this.warningReadState)
      }
      this.remoteWarningRows = nextRows
    },
    async fetchStaticData() {
      try {
        const [onDuty, maps, icons] = await Promise.all([
          getCenterTopOnDuty(),
          getCenterTopMaps(),
          getCenterTopIcons()
        ])
        this.remoteTopOnDuty = onDuty || null
        this.remoteTopMaps = Array.isArray(maps) ? maps : []
        this.remoteTopIcons = Array.isArray(icons) ? icons : []
      } catch (error) {
        console.error('中间顶部数据加载失败', error)
      }
    },
    async fetchPeriodData(showLoading = false) {
      const dateRange = this.dateRange || {}
      const requestId = ++this.warningRequestId
      if (showLoading) this.warningLoading = true
      try {
        const results = await Promise.all(this.warningTables.map(async table => {
          const page = this.warningPages[table.key]
          try {
            const data = await getCenterWarningPage(WARNING_PAGE_TYPES[table.key], dateRange, {
              pageNum: page.pageNum, pageSize: page.pageSize
            })
            return { key: table.key, data }
          } catch (error) {
            return { key: table.key, error }
          }
        }))
        if (requestId !== this.warningRequestId) {
          return
        }
        const nextRows = { ...this.remoteWarningRows }
        let needsRefetch = false
        results.forEach(({ key, data, error }) => {
          if (error) {
            this.$set(this.warningErrors, key, '加载失败，请稍后重试')
            return
          }
          this.$set(this.warningErrors, key, '')
          const page = this.warningPages[key]
          page.total = Math.max(0, Number(data.total) || 0)
          const lastPage = Math.max(1, Math.ceil(page.total / page.pageSize))
          if (page.pageNum > lastPage) {
            page.pageNum = lastPage
            needsRefetch = true
          }
          nextRows[key] = Array.isArray(data.rows) ? data.rows : []
        })
        if (needsRefetch) {
          await this.fetchPeriodData(showLoading)
          return
        }
        this.applyWarningRows(nextRows)
      } catch (error) {
        console.error('中间区域数据加载失败', error)
      } finally {
        if (requestId === this.warningRequestId) this.warningLoading = false
      }
    },
    async fetchShortcutData() {
      const period = this.shortcutPeriodKey || 'day'
      const dateRange = this.dateRange || {}
      const requestId = ++this.shortcutRequestId
      try {
        if (this.isShortcutDateRange) {
          const key = this.activeShortcutKey
          const range = this.shortcutDateRanges[key]
          if (!range || range.length !== 2) return
          const localRange = { startDate: range[0], endDate: range[1] }
          if (key === 'trade') {
            const [tradeRows, goodsTop] = await Promise.all([
              getCenterTradeRows('day', localRange),
              getCenterGoodsTop('day', localRange)
            ])
            if (requestId !== this.shortcutRequestId) return
            this.remoteTradeRows = tradeRows || null
            this.remoteGoodsTop = goodsTop || null
          } else {
            const rows = await getRightWorkRows('day', localRange)
            if (requestId !== this.shortcutRequestId) return
            this.remoteAuditRows = Array.isArray(rows) ? rows : []
          }
          return
        }
        const [tradeRows, goodsTop, taxationChart, railwayChart, barterChart, transferSummary, useSummary, grainChart, auditRows, dutyRows] = await Promise.all([
          getCenterTradeRows(period, dateRange),
          getCenterGoodsTop(period, dateRange),
          getLeftTaxationChart(period, dateRange),
          getLeftRailwayChart(period, dateRange),
          getLeftBarterChart(period, dateRange),
          getRightTransferSummary(period, dateRange),
          getRightUseSummary(period, dateRange),
          getRightGrainChart(period, dateRange),
          getRightWorkRows(period, dateRange),
          getRightDutyRows(period, dateRange)
        ])
        if (requestId !== this.shortcutRequestId) {
          return
        }
        this.remoteTradeRows = tradeRows || null
        this.remoteGoodsTop = goodsTop || null
        this.remoteTaxationChart = taxationChart || null
        this.remoteRailwayChart = railwayChart || null
        this.remoteBarterChart = barterChart || null
        this.remoteTransferSummary = transferSummary || null
        this.remoteUseSummary = useSummary || null
        this.remoteGrainChart = grainChart || null
        this.remoteAuditRows = Array.isArray(auditRows) ? auditRows : []
        this.remoteOnDutyRows = Array.isArray(dutyRows) ? dutyRows : []
        if (!this.remoteOnDutyRows.some(item => this.getDutyDepartmentId(item) === this.expandedDutyDeptId)) {
          this.expandedDutyDeptId = ''
        }
        this.$nextTick(() => this.refreshShortcutChart())
      } catch (error) {
        if (requestId === this.shortcutRequestId) {
          console.error('首页快捷弹窗数据加载失败', error)
        }
      }
    }
  }
}
</script>

<style scoped>
.center-panel {
  display: flex;
  flex-direction: column;
  padding-top: 0.46vh;
  gap: 0.34vh;
  min-height: 0;
}

.center-panel > .map-card {
  flex: 1.06 1 0;
}

.center-panel > .center-bottom {
  flex: 0.94 1 0;
}

.screen-card {
  position: relative;
  padding: 0.5vh 0.55vw 0.28vh;
  overflow: hidden;
}

.map-topline {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.96vw;
  margin-top: 0.06vh;
  margin-bottom: 0.26vh;
  color: #cfefff;
  font-size: 0.76vw;
  line-height: 1;
}

.duty-item {
  display: inline-flex;
  align-items: center;
  gap: 0.3vw;
  height: 2.08vh;
  min-width: 0;
}

.duty-text {
  display: inline-block;
  max-width: 12vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dot {
  width: 0.38vw;
  height: 0.38vw;
  border-radius: 50%;
  background: #17d6ff;
  box-shadow: 0 0 0.6vw rgba(23, 214, 255, 0.8);
}

.region-switch {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  justify-items: center;
  gap: clamp(8px, 0.6vw, 16px);
  width: min(100%, 92%);
  margin: 0.02vh auto 0.48vh;
}

.region-switch button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.34vw;
  isolation: isolate;
  overflow: hidden;
  height: 3.46vh;
  width: 100%;
  max-width: 7.24vw;
  min-width: 0;
  padding: 0 0.5vw;
  font-size: 0.8vw;
  font-weight: 700;
  border: 0.05vw solid rgba(72, 193, 255, 0.3);
  border-radius: 0.36vw;
  color: #eff9ff;
  letter-spacing: 0.01vw;
  text-shadow: 0 0 0.5vw rgba(185, 238, 255, 0.18);
  background:
    linear-gradient(180deg, rgba(48, 142, 244, 0.12), rgba(9, 47, 118, 0.04)),
    linear-gradient(180deg, rgba(21, 96, 214, 0.9), rgba(11, 63, 153, 0.78) 52%, rgba(8, 52, 130, 0.92));
  box-shadow:
    inset 0 0 0.84vw rgba(76, 196, 255, 0.16),
    inset 0 -0.14vh 0.38vh rgba(6, 25, 68, 0.32),
    0 0 0.88vw rgba(20, 121, 228, 0.18);
  cursor: pointer;
}

.region-switch__icon {
  width: 1.4vw;
  height: 1.4vw;
  flex: 0 0 auto;
  object-fit: contain;
}

.region-switch__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-switch button::before,
.region-switch button::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
}

.region-switch button::before {
  inset: 0.12vw;
  border: 0.05vw solid rgba(124, 233, 255, 0.3);
  box-shadow:
    inset 0 0 0.72vw rgba(101, 209, 255, 0.12),
    0 0 0.72vw rgba(71, 182, 255, 0.08);
}

.region-switch button::after {
  background:
    radial-gradient(circle at 50% 22%, rgba(178, 235, 255, 0.2), rgba(178, 235, 255, 0) 42%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0) 42%),
    radial-gradient(circle at 18% 76%, rgba(165, 239, 255, 0.16) 0 0.08vw, transparent 0.1vw),
    radial-gradient(circle at 82% 74%, rgba(165, 239, 255, 0.14) 0 0.07vw, transparent 0.09vw);
  opacity: 0.8;
}

.region-switch button.active {
  border-color: rgba(38, 255, 208, 0.4);
  color: #ecfffb;
  background:
    linear-gradient(180deg, rgba(77, 244, 214, 0.12), rgba(18, 115, 93, 0.02)),
    linear-gradient(180deg, rgba(39, 210, 171, 0.96), rgba(21, 161, 131, 0.9) 48%, rgba(13, 139, 112, 0.92));
  box-shadow:
    inset 0 0 1vw rgba(164, 255, 232, 0.2),
    inset 0 -0.18vh 0.42vh rgba(5, 61, 54, 0.22),
    0 0 1.12vw rgba(20, 238, 188, 0.28);
}

.region-switch button.active::before {
  border-color: rgba(71, 255, 229, 0.42);
  box-shadow:
    inset 0 0 0.82vw rgba(176, 255, 235, 0.18),
    0 0 0.84vw rgba(48, 255, 223, 0.16);
}

.region-switch button.active::after {
  background:
    radial-gradient(circle at 13% 74%, rgba(236, 255, 249, 0.78) 0 0.07vw, transparent 0.09vw),
    radial-gradient(circle at 20% 82%, rgba(236, 255, 249, 0.48) 0 0.06vw, transparent 0.08vw),
    radial-gradient(circle at 78% 72%, rgba(236, 255, 249, 0.4) 0 0.06vw, transparent 0.08vw),
    radial-gradient(circle at 88% 84%, rgba(236, 255, 249, 0.58) 0 0.07vw, transparent 0.09vw),
    linear-gradient(180deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0) 38%),
    radial-gradient(circle at 50% 22%, rgba(220, 255, 244, 0.18), rgba(220, 255, 244, 0) 44%);
}

.map-stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  border-radius: 0.8vw;
  background: transparent;
}

.map-image {
  position: absolute;
  top: 50%;
  left: calc(50% - 1.2vw);
  width: auto;
  height: 100%;
  max-width: none;
  transform: translate(-50%, -50%);
  filter: none;
}

.map-side-panel {
  position: absolute;
  right: 1.45vw;
  top: 22%;
  width: 8.6vw;
}

.map-left-description {
  position: absolute;
  left: 1.35vw;
  top: 12%;
  z-index: 2;
  width: 11.6vw;
  min-height: 15.8vh;
  padding: 0.76vw 0.7vw;
  border: 1px solid rgba(86, 187, 255, 0.24);
  border-radius: 0.5vw;
  background: linear-gradient(180deg, rgba(12, 72, 167, 0.4), rgba(6, 30, 84, 0.36));
  box-shadow: inset 0 0 0.9vw rgba(76, 196, 255, 0.08);
}

.map-left-description__text {
  height: 15.8vh;
  padding: 0.28vw 0.22vw;
  color: #ffffff;
  font-size: 0.64vw;
  line-height: 1.78;
  overflow-y: auto;
  white-space: pre-line;
  word-break: break-word;
  border-radius: 0.18vw;
}

.map-left-description__text::-webkit-scrollbar {
  width: 0.22vw;
}

.map-left-description__text::-webkit-scrollbar-thumb {
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(55, 214, 255, 0.92), rgba(16, 138, 255, 0.86));
  box-shadow: 0 0 0.4vw rgba(50, 195, 255, 0.22);
}

.map-left-description__text::-webkit-scrollbar-track {
  border-radius: 999vw;
  background: rgba(14, 48, 104, 0.42);
}

.map-side-tag {
  position: static;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.12vh;
  min-width: 0;
  width: 100%;
  padding: 0.3vw 0.3vw;
  border: 1px solid rgba(86, 187, 255, 0.3);
  border-radius: 0.5vw;
  color: #cdeeff;
  background: linear-gradient(180deg, rgba(19, 102, 232, 0.34), rgba(8, 43, 113, 0.28));
}

.side-tag-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 1.76vh;
  padding: 0.08vh 0.14vw;
  border: 1px solid transparent;
  border-radius: 0.34vw;
  color: #d7efff;
  cursor: pointer;
  background: transparent;
  transition: background-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.side-tag-title {
  font-size: 0.6vw;
  font-weight: 600;
}

.side-tag-subtitle {
  font-size: 0.58vw;
}

.side-tag-btn.active {
  color: #ffffff;
  background: linear-gradient(180deg, rgba(30, 163, 255, 0.9), rgba(16, 102, 220, 0.64));
  border-color: rgba(117, 218, 255, 0.35);
  box-shadow: inset 0 0 0.8vw rgba(140, 233, 255, 0.18), 0 0 0.8vw rgba(66, 200, 255, 0.18);
}

.side-tag-btn:not(.active) {
  color: #edf7ff;
  box-shadow: none;
}

.map-legend {
  position: absolute;
  left: 1.75vw;
  bottom: 2.2vh;
  display: grid;
  gap: 0.56vh;
  padding: 0;
  margin: 0;
  list-style: none;
  color: #d5efff;
  font-size: 0.68vw;
}

.map-legend li {
  display: inline-flex;
  align-items: center;
  gap: 0.55vw;
}

.legend-icon {
  width: 1.3vw;
  height: 1.3vw;
  flex: 0 0 1.3vw;
  object-fit: contain;
}

.legend-dot {
  width: 0.78vw;
  height: 0.78vw;
  flex: 0 0 0.78vw;
  transform: rotate(45deg);
  border-radius: 0.1vw;
  box-shadow: 0 0 0.6vw rgba(255, 255, 255, 0.25);
}

.map-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.center-bottom {
  display: grid;
  grid-template-rows: minmax(0, 1fr) clamp(72px, 8.8vh, 98px);
  gap: 1.12vh;
  min-height: 0;
}

.table-card.table-card--promoted {
  min-height: 0;
  z-index: 1;
}

.table-card--promoted .info-table-wrap {
  padding-bottom: 0.52vh;
}

.shortcut-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.68vw;
  min-height: 0;
  padding: 0.52vh 0.54vw 0.18vh;
  box-sizing: border-box;
  background: transparent;
}

.shortcut-card {
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  padding: 0 0.48vw;
  border: 0.05vw solid rgba(77, 195, 255, 0.3);
  border-radius: 0.28vw;
  color: #e9faff;
  font-size: 0.84vw;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0;
  text-shadow: none;
  background:
    linear-gradient(180deg, rgba(34, 150, 230, 0.18), rgba(8, 49, 119, 0.2)),
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.045) 0 0.38vw, rgba(255, 255, 255, 0.01) 0.38vw 0.76vw),
    linear-gradient(180deg, rgba(10, 67, 148, 0.86), rgba(5, 34, 91, 0.78));
  box-shadow: none;
  cursor: pointer;
}

.shortcut-card::before,
.shortcut-card::after {
  content: '';
  position: absolute;
  pointer-events: none;
}

.shortcut-card::before {
  inset: 0.16vw;
  border: 0.05vw solid rgba(120, 232, 255, 0.18);
  border-radius: 0.2vw;
}

.shortcut-card::after {
  left: 0.18vw;
  top: 18%;
  bottom: 18%;
  width: 0.14vw;
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(43, 232, 255, 0.98), rgba(21, 133, 255, 0.2));
  box-shadow: none;
}

.shortcut-card:hover,
.shortcut-card.active {
  color: #ffffff;
  border-color: rgba(92, 234, 255, 0.48);
  background:
    linear-gradient(180deg, rgba(40, 214, 255, 0.24), rgba(15, 95, 215, 0.16)),
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0 0.38vw, rgba(255, 255, 255, 0.01) 0.38vw 0.76vw),
    linear-gradient(180deg, rgba(12, 91, 190, 0.94), rgba(7, 54, 141, 0.88));
  box-shadow: none;
}

.shortcut-card__label {
  min-width: 0;
  text-align: center;
  white-space: nowrap;
}

.bottom-charts {
  display: flex;
  gap: clamp(10px, 0.6vw, 16px);
  padding: 0 0.72vw 0 0.62vw;
  min-height: 0;
  flex: 1.16 1 0;
}

.bottom-col-left,
.bottom-col-center,
.bottom-col-right,
.trade-block {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-top: 0.12vh;
}

.bottom-col-left {
  flex: 0 0 33%;
  padding-left: 0.06vw;
  padding-right: 0.2vw;
}

.bottom-col-left ::v-deep(.tab-header) {
  padding-left: 2.18vw;
}

.chart-period-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: clamp(76px, 4.6vw, 120px);
  height: 2.18vh;
  border: 0.05vw solid rgba(56, 177, 255, 0.32);
  border-radius: 0.46vw;
  background:
    linear-gradient(180deg, rgba(14, 86, 170, 0.5), rgba(6, 34, 90, 0.5)),
    rgba(4, 21, 62, 0.62);
  box-shadow:
    inset 0 0 0.88vw rgba(24, 162, 255, 0.12),
    0 0 0.68vw rgba(18, 132, 218, 0.12);
  overflow: hidden;
}

.chart-period-select-wrap::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0.56vw;
  width: 0.42vw;
  height: 0.24vw;
  background: rgba(238, 249, 255, 0.92);
  clip-path: polygon(0 0, 100% 0, 50% 100%);
  transform: translateY(-50%);
  pointer-events: none;
}

.chart-period-select {
  width: 100%;
  height: 100%;
  padding: 0 1.42vw 0 0.68vw;
  border: 0;
  color: rgba(214, 242, 255, 0.92);
  font-size: 0.58vw;
  font-weight: 600;
  line-height: 1;
  background: transparent;
  outline: none;
  appearance: none;
  cursor: pointer;
}

.chart-period-select option {
  color: #eaf8ff;
  background: #0a2f73;
}

.trade-block {
  flex: 1 1 auto;
  min-width: 0;
}

.trade-content {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 0.22vw;
  min-height: 0;
}

.bottom-col-center {
  flex: 0 0 70%;
  min-width: 0;
  padding-left: 0.02vw;
  padding-right: 0.12vw;
}

.bottom-col-right {
  flex: 0 0 28.5%;
  max-width: none;
  align-items: center;
  justify-content: center;
  padding-left: 0.24vw;
  padding-right: 0;
}

.center-panel ::v-deep(.tab-header) {
  align-items: center;
  height: 3.72vh;
  gap: 0.46vw;
  padding-top: 0;
  padding-right: 0.16vw;
  padding-bottom: 0;
  margin-bottom: 0.24vh;
}

.center-panel ::v-deep(.tab-text) {
  height: 100%;
  font-size: 0.8vw;
  font-weight: 700;
}

.center-panel ::v-deep(.tab-text.active) {
  color: #f1fbff;
}

.chart-unit {
  color: #6ab0d8;
  font-size: 0.62vw;
  padding-left: 0.35vw;
  margin-bottom: 0.16vh;
}

.filing-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.26vh 0.48vw;
  padding: 0 0.35vw;
  margin-bottom: 0.12vh;
}

.filing-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.22vw;
  color: #cceeff;
  font-size: 0.56vw;
  line-height: 1;
  white-space: nowrap;
}

.filing-legend-dot {
  width: 0.42vw;
  height: 0.42vw;
  border-radius: 50%;
  box-shadow: 0 0 0.42vw currentColor;
}

.bar-chart {
  flex: 1;
  min-height: 0;
}

.pie-tabs {
  display: inline-flex;
  justify-content: flex-end;
  gap: 0;
  margin-right: 0;
}

.trade-toolbar {
  display: inline-flex;
  align-items: center;
  gap: 0.32vw;
}

.tab-btn {
  min-width: 2.34vw;
  height: 2.32vh;
  padding: 0 0.68vw;
  font-size: 0.6vw;
}

.shortcut-dialog-layer {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 13, 36, 0.58);
}

.shortcut-dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  padding:
    clamp(104px, 5.6vw, 128px)
    clamp(48px, 2.7vw, 66px)
    clamp(30px, 2.8vw, 40px);
  border: 0;
  border-radius: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.shortcut-dialog__tools {
  position: absolute;
  top: clamp(30px, 3vh, 42px);
  right: clamp(40px, 3.5vw, 64px);
  z-index: 3;
  display: inline-flex;
  align-items: flex-end;
}

.shortcut-period-switch {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.12vw;
  width: clamp(150px, 10.2vw, 198px);
  padding: 0.16vh 0.18vw;
  border: 1px solid rgba(71, 186, 255, 0.34);
  border-radius: 999vw;
  background:
    linear-gradient(180deg, rgba(11, 78, 164, 0.7), rgba(5, 38, 98, 0.52)),
    rgba(3, 21, 60, 0.58);
  box-shadow: inset 0 0 0.7vw rgba(33, 177, 255, 0.12);
}

.shortcut-period-switch button {
  height: clamp(20px, 2vh, 26px);
  border: 0;
  border-radius: 999vw;
  color: rgba(220, 245, 255, 0.82);
  font-size: clamp(12px, 0.58vw, 14px);
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
}

.shortcut-period-switch button.active {
  color: #f6feff;
  background:
    linear-gradient(180deg, rgba(21, 210, 255, 0.44), rgba(14, 108, 235, 0.2)),
    linear-gradient(180deg, rgba(13, 91, 211, 0.98), rgba(8, 62, 158, 0.94));
  box-shadow:
    inset 0 0 0 1px rgba(119, 241, 255, 0.5),
    0 0 0.82vw rgba(32, 180, 255, 0.26);
}

.shortcut-dialog::before {
  content: none;
}

.shortcut-dialog :deep(.tab-header) {
  flex: 0 0 auto;
  padding-left: clamp(72px, 4.35vw, 92px);
  padding-right: 2.4vw;
}

.shortcut-dialog .trade-content--dialog {
  flex: 1;
  align-items: stretch;
  gap: clamp(18px, 1.65vw, 34px);
  min-height: 0;
  padding: 0.8vh 0.62vw 0.34vh;
  isolation: isolate;
}

.trade-network-panel,
.trade-funnel-panel {
  position: relative;
  min-width: 0;
  min-height: 0;
  box-shadow: none;
}

.trade-network-panel {
  flex: 1 1 63%;
  overflow: hidden;
}

.trade-funnel-panel {
  display: flex;
  flex: 0 0 35%;
  flex-direction: column;
  justify-content: center;
  padding: 1.1vh 0.4vw 0.4vh;
}

.trade-network {
  position: relative;
  width: min(100%, 46vw);
  height: 100%;
  margin: 0 auto;
  overflow: hidden;
  background:
    radial-gradient(circle, rgba(67, 189, 255, 0.1) 1px, transparent 1.5px);
  background-size: 0.62vw 0.62vw;
}

.trade-network::before {
  content: none;
}

.trade-network-center,
.trade-bubble {
  position: absolute;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #f5fdff;
  text-align: center;
  background:
    radial-gradient(circle at 34% 28%, rgba(111, 221, 255, 0.45), rgba(24, 126, 208, 0.26) 42%, rgba(7, 31, 80, 0.92) 100%);
  box-shadow:
    inset 0 0 0 1px rgba(131, 231, 255, 0.4),
    inset 0 -0.72vw 1.22vw rgba(0, 12, 44, 0.4);
}

.trade-network-center {
  left: 50%;
  top: 50%;
  width: clamp(112px, 7vw, 142px);
  height: clamp(112px, 7vw, 142px);
  font-size: clamp(18px, 1.18vw, 25px);
  font-weight: 800;
  transform: translate(-50%, -50%);
  background:
    linear-gradient(145deg, #0e7ed3 0%, #075cae 58%, #064285 100%);
  border: 1px solid rgba(148, 234, 255, 0.82);
  box-shadow:
    inset 0 0 0 0.36vw rgba(11, 114, 199, 0.68),
    inset 0 0 1.2vw rgba(160, 239, 255, 0.18);
}

.trade-network-center::before {
  content: '';
  position: absolute;
  inset: 10%;
  border: 1px solid rgba(180, 242, 255, 0.28);
  border-radius: 50%;
  pointer-events: none;
}

.trade-network-center::after {
  content: '';
  position: absolute;
  left: 24%;
  top: 19%;
  width: 24%;
  height: 12%;
  border-radius: 50%;
  background: rgba(202, 249, 255, 0.18);
  transform: rotate(-26deg);
  pointer-events: none;
}

.trade-bubble-group {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.trade-network-link {
  position: absolute;
  z-index: 2;
  height: 2px;
  background: linear-gradient(90deg, rgba(111, 226, 255, 0.78), rgba(111, 226, 255, 0.12));
  box-shadow: none;
  transform-origin: 0 50%;
}

.trade-bubble {
  transform: translate(-50%, -50%);
}

.trade-bubble strong {
  max-width: 82%;
  overflow: hidden;
  color: #ffffff;
  font-size: clamp(14px, 0.9vw, 20px);
  font-weight: 800;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trade-bubble span {
  margin-top: 0.44vh;
  color: #d9f8ff;
  font-size: clamp(11px, 0.62vw, 14px);
  line-height: 1;
}

.trade-bubble em {
  margin-top: 0.36vh;
  color: #8ee8ff;
  font-size: clamp(11px, 0.58vw, 13px);
  font-style: normal;
  line-height: 1;
}

.trade-funnel-title {
  flex: 0 0 auto;
  margin-bottom: 1.2vh;
  color: #effbff;
  font-size: clamp(18px, 1.1vw, 24px);
  font-weight: 800;
  text-align: center;
  text-shadow: none;
}

.trade-funnel {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: 0.8vh;
  width: 100%;
  min-height: 0;
}

.trade-funnel-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: clamp(52px, 6.7vh, 76px);
  min-width: 0;
  max-width: 100%;
  padding: 0 1vw;
  color: #ffffff;
  text-align: center;
  filter: none;
}

.trade-funnel-item::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0)),
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0 0.28vw, rgba(255, 255, 255, 0) 0.28vw 0.56vw);
  pointer-events: none;
}

.trade-funnel-name,
.trade-funnel-value {
  position: relative;
  z-index: 1;
  max-width: 82%;
  overflow: hidden;
  line-height: 1.12;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: none;
}

.trade-funnel-name {
  font-size: clamp(13px, 0.76vw, 17px);
  font-weight: 800;
}

.trade-funnel-value {
  margin-top: 0.26vh;
  font-size: clamp(13px, 0.82vw, 18px);
  font-weight: 700;
}

.trade-empty {
  position: absolute;
  left: 50%;
  top: 50%;
  color: rgba(220, 243, 255, 0.72);
  font-size: 0.82vw;
  transform: translate(-50%, -50%);
}

.trade-empty--funnel {
  position: static;
  transform: none;
}

.taxation-dialog-body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  padding: 0.26vh 0.34vw 0;
}

.taxation-chart {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.tianshan-dialog-body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  padding: 0.26vh 0.34vw 0;
}

.tianshan-chart {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.grain-dialog-body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  padding: 0.5vh 0.7vw 0.8vh;
}

.grain-dialog-chart {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.grain-details-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 0.68vh 0.7vw 0.6vh;
}

.grain-details-table {
  flex: 1;
  min-height: 0;
  padding-right: 0.24vw;
  color: #d7efff;
  overflow-y: auto;
}

.grain-details-table::-webkit-scrollbar,
.grain-transfer-panel::-webkit-scrollbar,
.grain-use-panel::-webkit-scrollbar {
  width: 0.24vw;
  height: 0.24vw;
}

.grain-details-table::-webkit-scrollbar-thumb,
.grain-transfer-panel::-webkit-scrollbar-thumb,
.grain-use-panel::-webkit-scrollbar-thumb {
  border-radius: 999vw;
  background: rgba(43, 200, 255, 0.62);
}

.grain-details-group {
  flex: 0 0 auto;
}

.grain-details-table__head,
.grain-details-table__row {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(86px, 0.72fr) minmax(150px, 1fr);
  gap: 0.5vw;
  align-items: center;
  padding: 0 0.72vw;
  text-align: center;
}

.grain-details-table__head {
  min-height: 3.2vh;
  color: rgba(191, 223, 246, 0.84);
  font-size: clamp(12px, 0.74vw, 16px);
  font-weight: 600;
}

.grain-details-table__row {
  position: relative;
  width: 100%;
  min-height: 5.1vh;
  border: 1px solid rgba(74, 193, 255, 0.18);
  border-radius: 0.18vw;
  color: inherit;
  background:
    linear-gradient(180deg, rgba(8, 34, 78, 0.46), rgba(4, 20, 49, 0.24)),
    linear-gradient(90deg, rgba(18, 92, 163, 0.08), rgba(18, 92, 163, 0));
  box-shadow: inset 0 0 0.82vw rgba(19, 126, 215, 0.06);
  font: inherit;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.grain-details-table__row:hover,
.grain-details-table__row.is-expanded {
  border-color: rgba(55, 216, 255, 0.48);
  background:
    linear-gradient(180deg, rgba(11, 65, 133, 0.72), rgba(4, 28, 70, 0.5)),
    linear-gradient(90deg, rgba(25, 143, 222, 0.14), rgba(18, 92, 163, 0));
}

.grain-details-table__row::before {
  content: '';
  position: absolute;
  left: 0.42vw;
  top: 0.8vh;
  bottom: 0.8vh;
  width: 0.12vw;
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(39, 227, 255, 1), rgba(39, 227, 255, 0.62));
  box-shadow: 0 0 0.52vw rgba(39, 227, 255, 0.28);
}

.grain-details-table__row > span,
.grain-details-table__row > strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.grain-details-table__row > span {
  color: #f0fbff;
  font-size: clamp(14px, 0.86vw, 18px);
}

.grain-details-table__row > strong {
  color: #22dcff;
  font-size: clamp(18px, 1.18vw, 25px);
  line-height: 1;
}

.grain-date-cell {
  display: flex;
  align-items: center;
  gap: 0.58vw;
  text-align: left;
}

.grain-expand-icon {
  flex: 0 0 auto;
  width: 0.48vw;
  height: 0.48vw;
  border-right: 0.1vw solid #65e8ff;
  border-bottom: 0.1vw solid #65e8ff;
  transform: rotate(-45deg);
  transition: transform 0.2s ease;
}

.grain-details-table__row.is-expanded .grain-expand-icon {
  transform: rotate(45deg) translateY(-0.08vw);
}

.grain-transfer-panel {
  max-height: 46vh;
  margin: 0.34vh 0.7vw 0.72vh;
  padding: 0.6vh 0.7vw 0.72vh;
  border: 1px solid rgba(63, 188, 255, 0.2);
  border-top: 0;
  border-radius: 0 0 0.36vw 0.36vw;
  background: rgba(3, 27, 70, 0.5);
  overflow: auto;
}

.grain-transfer-table,
.grain-use-table {
  min-width: 1080px;
}

.grain-transfer-table__head,
.grain-transfer-table__row {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr 1.45fr 0.95fr 1.08fr 0.92fr 0.92fr;
  gap: 0.46vw;
  align-items: center;
  min-height: 4.1vh;
  padding: 0 0.58vw;
  text-align: center;
}

.grain-transfer-table__head {
  color: rgba(179, 221, 247, 0.82);
  font-size: clamp(11px, 0.62vw, 14px);
  font-weight: 700;
}

.grain-transfer-table__row {
  border-top: 1px solid rgba(81, 182, 241, 0.14);
  color: #eefaff;
  font-size: clamp(11px, 0.66vw, 14px);
}

.grain-transfer-table__row.is-clickable {
  cursor: pointer;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.grain-transfer-table__row.is-clickable:hover,
.grain-transfer-table__row.is-clickable:focus-visible {
  color: #ffffff;
  background: rgba(28, 140, 214, 0.2);
  outline: none;
}

.grain-transfer-table__row > span,
.grain-use-table__row > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.grain-cell--wrap {
  overflow-wrap: anywhere !important;
  white-space: normal !important;
  line-height: 1.22;
}

.grain-use-panel {
  margin: 0.22vh 0 0.4vh;
  padding: 0.5vh 0.44vw 0.62vh;
  border-left: 1px solid rgba(63, 188, 255, 0.16);
  border-right: 1px solid rgba(63, 188, 255, 0.16);
  background: rgba(4, 34, 77, 0.42);
  overflow-x: auto;
}

.grain-use-table {
  min-width: 1260px;
}

.grain-use-table__head,
.grain-use-table__row {
  display: grid;
  grid-template-columns: 0.54fr 0.96fr 1.02fr 1.32fr 0.88fr 1.12fr 1.04fr 0.96fr 1.04fr 0.96fr;
  gap: 0.42vw;
  align-items: center;
  min-height: 3.5vh;
  padding: 0 0.5vw;
  text-align: center;
}

.grain-use-table__head {
  color: rgba(155, 212, 242, 0.74);
  font-size: clamp(10px, 0.58vw, 13px);
  font-weight: 700;
}

.grain-use-table__row {
  border-top: 1px solid rgba(81, 182, 241, 0.12);
  color: #eaf8ff;
  font-size: clamp(10px, 0.6vw, 13px);
}

.grain-detail-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 6vh;
  color: rgba(206, 235, 251, 0.7);
  font-size: clamp(12px, 0.7vw, 15px);
}

.grain-detail-pagination {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 0.7vw;
  padding-top: 1vh;
  color: #cdeeff;
  font-size: clamp(12px, 0.68vw, 14px);
}

.grain-detail-pagination button {
  min-width: 74px;
  height: 3.4vh;
  border: 1px solid rgba(112, 224, 255, 0.46);
  border-radius: 6px;
  color: #f2fcff;
  background: linear-gradient(180deg, rgba(34, 168, 242, 0.92), rgba(11, 90, 185, 0.88));
  cursor: pointer;
}

.grain-detail-pagination button:disabled {
  opacity: 0.42;
  cursor: not-allowed;
}

.barter-dialog-body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  padding: 0.26vh 0.34vw 0;
}

.barter-chart {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.onduty-dialog-body {
  display: flex;
  flex: 1;
  min-height: 0;
  padding: 1.2vh 1vw 0.6vh;
}

.onduty-table {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  gap: 0.42vh;
  padding-right: 0.28vw;
  color: #d7efff;
  overflow-y: auto;
}

.onduty-table::-webkit-scrollbar,
.onduty-expand-panel::-webkit-scrollbar {
  width: 0.24vw;
}

.onduty-table::-webkit-scrollbar-thumb,
.onduty-expand-panel::-webkit-scrollbar-thumb {
  border-radius: 999vw;
  background: rgba(43, 200, 255, 0.62);
}

.onduty-table__group {
  flex: 0 0 auto;
}

.onduty-table__header,
.onduty-table__row {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(130px, 1.2fr) minmax(105px, 0.96fr) minmax(100px, 0.86fr);
  gap: 0.5vw;
  align-items: center;
  padding: 0 0.72vw;
}

.onduty-table__header {
  min-height: 3.2vh;
  color: rgba(191, 223, 246, 0.84);
  font-size: clamp(12px, 0.74vw, 16px);
  font-weight: 600;
  text-align: center;
}

.onduty-table__row {
  position: relative;
  width: 100%;
  flex: 0 0 auto;
  min-height: 5.1vh;
  padding: 0 0.72vw;
  border: 1px solid rgba(74, 193, 255, 0.18);
  border-radius: 0.18vw;
  color: inherit;
  background:
    linear-gradient(180deg, rgba(8, 34, 78, 0.46), rgba(4, 20, 49, 0.24)),
    linear-gradient(90deg, rgba(18, 92, 163, 0.08), rgba(18, 92, 163, 0));
  box-shadow: inset 0 0 0.82vw rgba(19, 126, 215, 0.06);
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.onduty-table__row:hover,
.onduty-table__row.is-expanded {
  border-color: rgba(55, 216, 255, 0.48);
  background:
    linear-gradient(180deg, rgba(11, 65, 133, 0.72), rgba(4, 28, 70, 0.5)),
    linear-gradient(90deg, rgba(25, 143, 222, 0.14), rgba(18, 92, 163, 0));
}

.onduty-table__row::before {
  content: '';
  position: absolute;
  left: 0.42vw;
  top: 0.8vh;
  bottom: 0.8vh;
  width: 0.12vw;
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(39, 227, 255, 1), rgba(39, 227, 255, 0.62));
  box-shadow: 0 0 0.52vw rgba(39, 227, 255, 0.28);
}

.onduty-table__row span {
  min-width: 0;
  overflow: hidden;
  color: #f0fbff;
  font-size: clamp(14px, 0.86vw, 18px);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.onduty-table__row strong {
  color: #22dcff;
  font-size: clamp(18px, 1.18vw, 25px);
  line-height: 1;
  text-shadow: 0 0 0.7vw rgba(34, 220, 255, 0.18);
}

.onduty-dept-cell {
  display: flex;
  align-items: center;
  gap: 0.58vw;
  text-align: left;
}

.onduty-expand-icon {
  flex: 0 0 auto;
  width: 0.48vw;
  height: 0.48vw;
  border-right: 0.1vw solid #65e8ff;
  border-bottom: 0.1vw solid #65e8ff;
  transform: rotate(-45deg);
  transition: transform 0.2s ease;
}

.onduty-table__row.is-expanded .onduty-expand-icon {
  transform: rotate(45deg) translateY(-0.08vw);
}

.onduty-expand-panel {
  max-height: 28vh;
  margin: 0.34vh 0.7vw 0.72vh;
  padding: 0.6vh 0.7vw 0.72vh;
  border: 1px solid rgba(63, 188, 255, 0.2);
  border-top: 0;
  border-radius: 0 0 0.36vw 0.36vw;
  background: rgba(3, 27, 70, 0.5);
  overflow-y: auto;
}

.onduty-detail-head,
.onduty-detail-row,
.onduty-person-row {
  display: grid;
  align-items: center;
  gap: 0.5vw;
  min-height: 3.5vh;
  padding: 0 0.7vw;
  text-align: center;
}

.onduty-detail-grid--daily {
  grid-template-columns: minmax(0, 1fr) minmax(90px, 0.8fr) minmax(120px, 0.9fr);
}

.onduty-detail-grid--people {
  grid-template-columns: minmax(0, 1fr) minmax(76px, 0.62fr) minmax(90px, 0.8fr) minmax(100px, 0.72fr);
}

.onduty-detail-head {
  color: rgba(179, 221, 247, 0.82);
  font-size: clamp(11px, 0.66vw, 14px);
  font-weight: 700;
}

.onduty-detail-row,
.onduty-person-row {
  border-top: 1px solid rgba(81, 182, 241, 0.14);
  color: #eefaff;
  font-size: clamp(12px, 0.72vw, 15px);
}

.onduty-person-row {
  width: 100%;
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  background: transparent;
  cursor: pointer;
}

.onduty-person-row:hover {
  background: rgba(29, 139, 218, 0.16);
}

.onduty-person-row strong {
  color: #35deff;
  font-size: clamp(14px, 0.86vw, 18px);
}

.onduty-person-type {
  color: rgba(225, 247, 255, 0.92);
  font-weight: 600;
}

.onduty-record-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.36vw;
  color: #82ddff;
}

.onduty-record-link i {
  width: 0.38vw;
  height: 0.38vw;
  border-top: 0.08vw solid currentColor;
  border-right: 0.08vw solid currentColor;
  transform: rotate(45deg);
}

.onduty-detail-empty,
.duty-record-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 6vh;
  color: rgba(206, 235, 251, 0.7);
  font-size: clamp(12px, 0.7vw, 15px);
}

.duty-status {
  font-weight: 700;
}

.duty-status--active {
  color: #5bf0b2 !important;
}

.duty-status--out {
  color: #64dcff !important;
}

.duty-status--leave {
  color: #ffd06b !important;
}

.duty-record-layer {
  position: fixed;
  inset: 0;
  z-index: 92;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(1, 10, 29, 0.62);
}

.duty-record-dialog {
  display: flex;
  flex-direction: column;
  width: min(760px, 58vw);
  height: min(620px, 66vh);
  min-width: 560px;
  padding: clamp(18px, 1.35vw, 28px);
  border: 1px solid rgba(76, 207, 255, 0.5);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(8, 61, 134, 0.98), rgba(3, 27, 72, 0.98)),
    #05295f;
  box-shadow: 0 1.2vw 3vw rgba(0, 5, 24, 0.52), inset 0 0 2vw rgba(37, 170, 255, 0.12);
}

.duty-record-dialog__header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 1.4vh;
  border-bottom: 1px solid rgba(91, 205, 255, 0.24);
}

.duty-record-dialog__header div {
  display: flex;
  flex-direction: column;
  gap: 0.42vh;
}

.duty-record-dialog__header span {
  color: rgba(172, 224, 250, 0.74);
  font-size: clamp(12px, 0.66vw, 14px);
}

.duty-record-dialog__header strong {
  color: #ffffff;
  font-size: clamp(18px, 1.08vw, 24px);
}

.duty-record-dialog__close {
  width: clamp(32px, 2vw, 40px);
  height: clamp(32px, 2vw, 40px);
  padding: 0;
  border: 1px solid rgba(106, 216, 255, 0.34);
  border-radius: 50%;
  color: #dff7ff;
  font-size: clamp(22px, 1.35vw, 28px);
  line-height: 1;
  background: rgba(14, 94, 175, 0.42);
  cursor: pointer;
}

.duty-record-table {
  flex: 1;
  min-height: 0;
  margin-top: 1.4vh;
  overflow-y: auto;
}

.duty-record-table__head,
.duty-record-table__row {
  display: grid;
  grid-template-columns: 0.55fr 1fr 1fr 1.2fr;
  align-items: center;
  min-height: 4.7vh;
  padding: 0 0.8vw;
  text-align: center;
}

.duty-record-table__head {
  color: #9eddf8;
  font-size: clamp(12px, 0.68vw, 14px);
  font-weight: 700;
  background: rgba(22, 115, 194, 0.22);
}

.duty-record-table__row {
  border-bottom: 1px solid rgba(82, 190, 241, 0.14);
  color: #f0faff;
  font-size: clamp(12px, 0.74vw, 16px);
}

.duty-record-pagination {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 0.7vw;
  padding-top: 1.4vh;
  color: #cdeeff;
  font-size: clamp(12px, 0.68vw, 14px);
}

.duty-record-pagination button {
  min-width: 74px;
  height: 3.4vh;
  border: 1px solid rgba(112, 224, 255, 0.46);
  border-radius: 6px;
  color: #f2fcff;
  background: linear-gradient(180deg, rgba(34, 168, 242, 0.92), rgba(11, 90, 185, 0.88));
  cursor: pointer;
}

.duty-record-pagination button:disabled {
  opacity: 0.42;
  cursor: not-allowed;
}

.grain-use-record-layer {
  position: fixed;
  inset: 0;
  z-index: 94;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(1, 10, 29, 0.68);
}

.grain-use-record-dialog {
  display: flex;
  flex-direction: column;
  width: min(1180px, 86vw);
  height: min(650px, 70vh);
  min-width: 0;
  padding: clamp(18px, 1.35vw, 28px);
  border: 1px solid rgba(76, 207, 255, 0.5);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(8, 61, 134, 0.98), rgba(3, 27, 72, 0.98)),
    #05295f;
  box-shadow: 0 1.2vw 3vw rgba(0, 5, 24, 0.52), inset 0 0 2vw rgba(37, 170, 255, 0.12);
}

.grain-use-record-dialog__header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 1vw;
  padding-bottom: 1.4vh;
  border-bottom: 1px solid rgba(91, 205, 255, 0.24);
}

.grain-use-record-dialog__header div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.42vh;
}

.grain-use-record-dialog__header span {
  color: rgba(172, 224, 250, 0.74);
  font-size: clamp(12px, 0.66vw, 14px);
}

.grain-use-record-dialog__header strong {
  max-width: 100%;
  overflow: hidden;
  color: #ffffff;
  font-size: clamp(18px, 1.08vw, 24px);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.grain-use-record-dialog__close {
  flex: 0 0 auto;
  width: clamp(32px, 2vw, 40px);
  height: clamp(32px, 2vw, 40px);
  padding: 0;
  border: 1px solid rgba(106, 216, 255, 0.34);
  border-radius: 50%;
  color: #dff7ff;
  font-size: clamp(22px, 1.35vw, 28px);
  line-height: 1;
  background: rgba(14, 94, 175, 0.42);
  cursor: pointer;
}

.grain-use-record-table {
  flex: 1;
  min-height: 0;
  margin-top: 1.4vh;
  overflow: auto;
}

.grain-use-record-table__inner {
  min-width: 1100px;
}

.grain-use-record-table__head,
.grain-use-record-table__row {
  display: grid;
  grid-template-columns: 0.9fr 1.15fr 1.35fr 0.9fr 1.12fr 1.08fr 0.98fr 1.05fr 0.95fr;
  align-items: center;
  gap: 0.45vw;
  min-height: 4.7vh;
  padding: 0 0.8vw;
  text-align: center;
}

.grain-use-record-table__head {
  color: #9eddf8;
  font-size: clamp(11px, 0.68vw, 14px);
  font-weight: 700;
  background: rgba(22, 115, 194, 0.22);
}

.grain-use-record-table__row {
  border-bottom: 1px solid rgba(82, 190, 241, 0.14);
  color: #f0faff;
  font-size: clamp(11px, 0.68vw, 15px);
}

.grain-use-record-table__row > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.grain-record-cell--wrap {
  overflow-wrap: anywhere !important;
  white-space: normal !important;
  line-height: 1.22;
}

.grain-use-record-pagination {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 0.7vw;
  padding-top: 1.4vh;
  color: #cdeeff;
  font-size: clamp(12px, 0.68vw, 14px);
}

.grain-use-record-pagination button {
  min-width: 74px;
  height: 3.4vh;
  border: 1px solid rgba(112, 224, 255, 0.46);
  border-radius: 6px;
  color: #f2fcff;
  background: linear-gradient(180deg, rgba(34, 168, 242, 0.92), rgba(11, 90, 185, 0.88));
  cursor: pointer;
}

.grain-use-record-pagination button:disabled {
  opacity: 0.42;
  cursor: not-allowed;
}

.shortcut-blank {
  flex: 1;
  min-height: 0;
}

.shortcut-empty {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 0;
  color: rgba(214, 239, 255, 0.78);
  font-size: 0.88vw;
}

.audit-dialog-panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 1.2vh 1vw 0.6vh;
}

.audit-summary-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.88vw;
  flex: 0 0 auto;
  margin-bottom: 1.25vh;
}

.audit-summary-card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 6.2vh;
  padding: 0 1.2vw;
  border: 1px solid rgba(72, 188, 255, 0.22);
  background:
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0 0.34vw, rgba(255, 255, 255, 0) 0.34vw 0.68vw),
    linear-gradient(90deg, rgba(8, 75, 142, 0.52), rgba(4, 33, 88, 0.72));
}

.audit-summary-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.8vh;
  bottom: 0.8vh;
  width: 0.16vw;
  background: #42d9ff;
}

.audit-summary-card--done::before {
  background: #16e0c5;
}

.audit-summary-card--total::before {
  background: #8ff478;
}

.audit-summary-card span {
  color: rgba(195, 235, 255, 0.86);
  font-size: 0.86vw;
  font-weight: 700;
}

.audit-summary-card strong {
  color: #f5fcff;
  font-size: clamp(24px, 1.9vw, 38px);
  font-weight: 800;
  line-height: 1;
}

.audit-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.12vh 0.9vw;
  flex: 1;
  min-height: 0;
}

.audit-item-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  padding: 1vh 0.95vw;
  border: 1px solid rgba(74, 192, 255, 0.18);
  background:
    linear-gradient(180deg, rgba(10, 76, 151, 0.5), rgba(4, 28, 79, 0.56)),
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.035) 0 0.34vw, rgba(255, 255, 255, 0) 0.34vw 0.68vw);
}

.audit-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8vw;
  margin-bottom: 1.1vh;
}

.audit-item-title {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  color: #eafcff;
  font-size: clamp(17px, 1vw, 22px);
  font-weight: 800;
  white-space: nowrap;
}

.audit-item-title i {
  display: inline-block;
  width: 0.24vw;
  height: 2.3vh;
  margin-right: 0.52vw;
  border-radius: 999vw;
}

.audit-item-total {
  color: rgba(161, 218, 255, 0.86);
  font-size: 0.72vw;
  white-space: nowrap;
}

.audit-metric-row {
  display: grid;
  grid-template-columns: 2.5vw minmax(0, 1fr) 3.2vw;
  align-items: center;
  gap: 0.72vw;
  min-height: 3vh;
  color: rgba(211, 242, 255, 0.9);
  font-size: 0.72vw;
}

.audit-metric-row + .audit-metric-row {
  margin-top: 0.78vh;
}

.audit-metric-row strong {
  color: #ffffff;
  font-size: 1vw;
  text-align: right;
}

.audit-meter {
  height: 0.86vh;
  overflow: hidden;
  border-radius: 999vw;
  background: rgba(7, 64, 125, 0.72);
}

.audit-meter i {
  display: block;
  height: 100%;
  min-width: 0;
  border-radius: inherit;
}

.table-card {
  display: flex;
  flex-direction: column;
  flex: 0.84 1 0;
  min-height: 0;
  padding: 0.08vh 0.42vw 0.18vh;
  overflow: hidden;
}

.table-card ::v-deep(.tab-header) {
  padding-left: 4.82vw;
}

.table-tabs {
  display: flex;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 0;
  margin-left: auto;
}

.table-tab {
  min-width: 4.2vw;
  height: 2.32vh;
  padding: 0 0.68vw;
  font-size: 0.6vw;
}

.table-tab__new-dot {
  position: absolute;
  top: 0.2vh;
  right: 0.28vw;
  width: clamp(5px, 0.32vw, 7px);
  height: clamp(5px, 0.32vw, 7px);
  border-radius: 50%;
  background: #ff3b3b;
  box-shadow: 0 0 0.42vw rgba(255, 59, 59, 0.92);
  pointer-events: none;
}

.info-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.06vw;
}

.info-table-wrap::-webkit-scrollbar {
  width: 0.3vw;
  height: 0.3vw;
}

.warning-empty {
  height: 72px;
  text-align: center;
}

.warning-pagination {
  flex: 0 0 auto;
  align-self: flex-end;
  max-width: 100%;
  padding: 6px 0 2px;
  white-space: normal;
}

.warning-pagination ::v-deep(.el-pagination__total),
.warning-pagination ::v-deep(.el-pagination__jump) {
  color: #b7deff;
}

.warning-pagination ::v-deep(button),
.warning-pagination ::v-deep(.el-pager li),
.warning-pagination ::v-deep(.el-input__inner) {
  border: 1px solid rgba(77, 167, 235, 0.32);
  color: #c5e7ff;
  background: rgba(6, 46, 102, 0.7);
}

.warning-pagination ::v-deep(.el-pager li.active) {
  color: #fff;
  background: #1379c4;
}

.warning-pagination ::v-deep(button:disabled) {
  color: #5c7eab;
}

.info-table-wrap::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(92, 204, 255, 0.4);
}

.info-table-wrap::-webkit-scrollbar-track {
  background: rgba(12, 49, 110, 0.18);
}

.info-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 0.44vh;
  color: #d9f0ff;
  table-layout: fixed;
  margin-top: 0.12vh;
}

.info-table thead th {
  position: sticky;
  top: 0;
  z-index: 6;
  box-sizing: border-box;
  padding: 0.66vh 0.36vw;
  color: #8fd9ff;
  font-weight: 600;
  text-align: center;
  font-size: 0.66vw;
  background:
    radial-gradient(circle at center, rgba(255, 255, 255, 0.08) 0, rgba(255, 255, 255, 0) 70%),
    linear-gradient(180deg, #1058ad, #082e6f);
  border-top: 1px solid rgba(91, 198, 255, 0.28);
  border-bottom: 1px solid rgba(91, 198, 255, 0.2);
  background-clip: padding-box;
  box-shadow:
    0 -1.1vh 0 1.1vh #062a63,
    0 0.36vh 0.52vh rgba(2, 13, 36, 0.36);
}

.info-table thead th:first-child {
  border-left: 1px solid rgba(91, 198, 255, 0.24);
  border-top-left-radius: 0.12vw;
  border-bottom-left-radius: 0.12vw;
}

.info-table thead th:last-child {
  border-right: 1px solid rgba(91, 198, 255, 0.24);
  border-top-right-radius: 0.12vw;
  border-bottom-right-radius: 0.12vw;
}

.info-table tbody td {
  box-sizing: border-box;
  padding: 0.62vh 0.36vw;
  text-align: center;
  font-size: 0.66vw;
  line-height: 1.35;
  background: linear-gradient(180deg, rgba(10, 43, 99, 0.62), rgba(7, 31, 73, 0.38));
  border-top: 1px solid rgba(91, 198, 255, 0.22);
  border-bottom: 1px solid rgba(91, 198, 255, 0.16);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info-table tbody td:first-child {
  border-left: 1px solid rgba(91, 198, 255, 0.24);
  border-top-left-radius: 0.1vw;
  border-bottom-left-radius: 0.1vw;
}

.info-table tbody td:last-child {
  border-right: 1px solid rgba(91, 198, 255, 0.24);
  border-top-right-radius: 0.1vw;
  border-bottom-right-radius: 0.1vw;
}

.info-table tbody tr:nth-child(odd) {
  background: transparent;
}

.info-table-col--left {
  text-align: left !important;
}

.info-table-col--wrap {
  white-space: normal !important;
  word-break: break-all;
  overflow-wrap: anywhere;
}

.info-table-col--small {
  font-size: 0.6vw !important;
  letter-spacing: 0;
}
</style>
